import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Container, Typography, Box, CircularProgress, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Checkbox, Skeleton, Switch, GlobalStyles, Slider } from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import CodeIcon from '@mui/icons-material/Code';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ScaleIcon from '@mui/icons-material/Scale';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InventoryIcon from '@mui/icons-material/Inventory';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ListIcon from '@mui/icons-material/List';
import MapIcon from '@mui/icons-material/Map';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import DescriptionIcon from '@mui/icons-material/Description';
import NotesIcon from '@mui/icons-material/Notes';
import BadgeIcon from '@mui/icons-material/Badge';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import DirectionsIcon from '@mui/icons-material/Directions';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import IconButton from '@mui/material/IconButton';
import { Tabs, Tab, Popover } from '@mui/material';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { YMaps, Map, Placemark, Polyline, ZoomControl, GeolocationControl } from '@pbe/react-yandex-maps';

import { deploymentName, moscowDate, gpsStatusText } from "./runtimeConfig";

// Создаем React Query клиент
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000, // 1 минута
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        return failureCount < 3 && error?.response?.status !== 404;
      },
    },
  },
});

// Функция маппинга статусов из 1С в отображаемые статусы
const mapRouteStatus = (status1C: string): string => {
  switch (status1C) {
    case 'Формируется':
    case 'КПогрузке':
      return 'Ожидание';
    case 'Отправлено':
      return 'В пути';
    case 'Закрыто':
      return 'Завершено';
    default:
      return status1C; // Возвращаем как есть если статус неизвестен
  }
};

// Функция для определения цвета статуса доставки
const getDeliveryStatusColor = (delivery: any): string => {
  if (delivery.deliveryReport?.includes('Доставлено')) {
    return '#4caf50'; // Зеленый - доставлено
  } else if (delivery.deliveryReport?.includes('Попытка доставки')) {
    return '#f44336'; // Красный - не приняли
  } else {
    return '#9e9e9e'; // Серый - доставляется (нет отчета)
  }
};

// Функция для получения текста и эмодзи статуса доставки
const getDeliveryStatusText = (delivery: any): { text: string; emoji: string } => {
  if (delivery.deliveryReport?.includes('Доставлено')) {
    return { text: 'Доставлено', emoji: '✅' };
  } else if (delivery.deliveryReport?.includes('Попытка доставки')) {
    return { text: 'Не приняли', emoji: '❌' };
  } else {
    return { text: 'Доставляется', emoji: '⏳' };
  }
};

const WAREHOUSE_COORDINATES: [number, number] = [42.883564, 47.618345];

const getPointDistanceMeters = (from: [number, number], to: [number, number]): number => {
  const radius = 6371000;
  const lat1 = from[0] * Math.PI / 180;
  const lat2 = to[0] * Math.PI / 180;
  const deltaLat = (to[0] - from[0]) * Math.PI / 180;
  const deltaLon = (to[1] - from[1]) * Math.PI / 180;
  const a = Math.sin(deltaLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) ** 2;
  return radius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const getBearingDegrees = (from: [number, number], to: [number, number]): number => {
  const lat1 = from[0] * Math.PI / 180;
  const lat2 = to[0] * Math.PI / 180;
  const deltaLon = (to[1] - from[1]) * Math.PI / 180;
  const y = Math.sin(deltaLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLon);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
};

const formatTrackPointTime = (value: any): string => {
  if (value === null || value === undefined || value === '') return 'не указано';
  if (typeof value === 'number' || /^\d+$/.test(String(value))) {
    const numeric = Number(value);
    const millis = numeric < 1000000000000 ? numeric * 1000 : numeric;
    return new Date(millis).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return String(value);
};

const isMovingTrackPoint = (point: any): boolean => {
  const speed = Number(point?.speed);
  return Number.isFinite(speed) && speed > 3;
};

const getDeliveryCoordinates = (delivery: any): [number, number] | null => {
  const lat = Number(delivery?.location?.latitude);
  const lon = Number(delivery?.location?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  return [lat, lon];
};

const isDeliveryCompleted = (delivery: any): boolean => {
  return Boolean(delivery?.deliveryReport?.includes('Доставлено'));
};

const getDriverInsertIndex = (route: any, vehicle: any): number | null => {
  const vehicleLat = Number(vehicle?.lat);
  const vehicleLon = Number(vehicle?.lon);
  if (!Number.isFinite(vehicleLat) || !Number.isFinite(vehicleLon)) return null;

  const deliveries = route?.deliveries || [];
  const vehiclePoint: [number, number] = [vehicleLat, vehicleLon];
  let nearest = {
    type: 'warehouse',
    index: -1,
    distance: getPointDistanceMeters(vehiclePoint, WAREHOUSE_COORDINATES),
  };

  deliveries.forEach((delivery: any, index: number) => {
    const coordinates = getDeliveryCoordinates(delivery);
    if (!coordinates) return;

    const distance = getPointDistanceMeters(vehiclePoint, coordinates);
    if (distance < nearest.distance) {
      nearest = { type: 'delivery', index, distance };
    }
  });

  if (nearest.type === 'warehouse') {
    const completedCount = deliveries.filter(isDeliveryCompleted).length;
    return completedCount === 0 ? 0 : deliveries.length;
  }

  return nearest.index;
};

// Функция для создания SVG маркера с badge-кружком и иконкой
const createMarkerWithBadge = (number: string, routeColor: string, statusColor: string, delivery: any): string => {
  // Определяем иконку внутри badge в зависимости от статуса
  let badgeIcon = '';
  if (delivery.deliveryReport?.includes('Доставлено')) {
    // Зеленая галочка (V-образная)
    badgeIcon = '<path d="M29 8 L31 10 L35 5" stroke="white" stroke-width="1.8" fill="none" stroke-linecap="round" stroke-linejoin="round"/>';
  } else if (delivery.deliveryReport?.includes('Попытка доставки')) {
    // Красный крестик
    badgeIcon = '<line x1="29" y1="5" x2="35" y2="11" stroke="white" stroke-width="1.8" stroke-linecap="round"/><line x1="35" y1="5" x2="29" y2="11" stroke="white" stroke-width="1.8" stroke-linecap="round"/>';
  } else {
    // Серая точка
    badgeIcon = '<circle cx="32" cy="8" r="2.5" fill="white"/>';
  }

  const svg = `
    <svg width="40" height="52" xmlns="http://www.w3.org/2000/svg">
      <!-- Основной маркер -->
      <path d="M20,2 C12,2 6,8 6,16 C6,26 20,50 20,50 C20,50 34,26 34,16 C34,8 28,2 20,2 Z"
            fill="${routeColor}" stroke="white" stroke-width="2"/>

      <!-- Круг внутри маркера -->
      <circle cx="20" cy="16" r="10" fill="white"/>

      <!-- Номер точки -->
      <text x="20" y="21" text-anchor="middle" font-size="12" font-weight="bold" fill="${routeColor}">${number}</text>

      <!-- Badge-кружок со статусом -->
      <circle cx="32" cy="8" r="6" fill="${statusColor}" stroke="white" stroke-width="2"/>

      <!-- Иконка статуса -->
      ${badgeIcon}
    </svg>
  `;
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
};

// Функция для создания SVG маркера склада
const createWarehouseMarker = (): string => {
  const svg = `
    <svg width="40" height="52" xmlns="http://www.w3.org/2000/svg">
      <!-- Основной маркер -->
      <path d="M20,2 C12,2 6,8 6,16 C6,26 20,50 20,50 C20,50 34,26 34,16 C34,8 28,2 20,2 Z"
            fill="#212121" stroke="white" stroke-width="2"/>

      <!-- Иконка склада (упрощенная) -->
      <!-- Крыша склада -->
      <path d="M12 14 L20 10 L28 14" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>

      <!-- Стены склада -->
      <rect x="13" y="14" width="14" height="8" fill="white" stroke="white" stroke-width="0.5"/>

      <!-- Ворота склада -->
      <rect x="16" y="17" width="8" height="5" fill="#212121"/>

      <!-- Горизонтальная линия ворот -->
      <line x1="16" y1="19.5" x2="24" y2="19.5" stroke="white" stroke-width="0.8"/>
    </svg>
  `;
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
};

// Функция создания SVG маркера текущего положения машины
const createVehicleMarker = (routeColor: string, opacity = 1): string => {
  const svg = `
    <svg width="54" height="74" viewBox="0 0 54 74" xmlns="http://www.w3.org/2000/svg" opacity="${opacity}">
      <defs>
        <radialGradient id="innerGlow" cx="50%" cy="42%" r="58%">
          <stop offset="0" stop-color="#ffffff"/>
          <stop offset="0.66" stop-color="#f8fafc"/>
          <stop offset="1" stop-color="#d1d5db"/>
        </radialGradient>
        <linearGradient id="pinShade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="${routeColor}"/>
          <stop offset="1" stop-color="${routeColor}" stop-opacity="0.86"/>
        </linearGradient>
      </defs>
      <path d="M27 72 C27 72 49 44 49 25 C49 11.8 39.2 2 27 2 C14.8 2 5 11.8 5 25 C5 44 27 72 27 72 Z"
            fill="url(#pinShade)" stroke="white" stroke-width="2.5"/>
      <circle cx="27" cy="25" r="18.5" fill="url(#innerGlow)" stroke="white" stroke-width="2"/>
      <path d="M 12.011 24.298 L 14.611 15.798 C 15.311 13.598 17.111 12.198 19.511 12.198 L 34.911 12.198 C 37.311 12.198 39.111 13.598 39.811 15.798 L 42.411 24.298 L 42.411 30.598 L 42.197 33.174 C 41.597 35.574 38.676 34.821 36.176 34.821 C 33.676 34.821 34.751 33.232 34.151 30.832 L 20.093 30.876 C 19.493 33.276 20.82 35.132 18.32 35.132 C 15.82 35.132 12.705 35.811 12.105 33.411 L 12.011 30.598 L 12.011 24.298 Z"
            fill="${routeColor}"/>
      <path d="M 19.889 13.94 L 34.889 13.94 C 35.889 13.94 36.689 14.54 37.089 15.54 L 38.389 19.94 C 34.989 19.14 31.389 18.74 27.389 18.74 C 23.389 18.74 19.789 19.14 16.389 19.94 L 17.689 15.54 C 18.089 14.54 18.889 13.94 19.889 13.94 Z"
            fill="white"/>
      <path d="M 14.118 22.368 C 17.218 22.868 19.918 23.668 22.418 24.968 C 20.318 26.168 17.118 26.168 14.518 25.168 L 14.118 22.368 Z" fill="white"/>
      <path d="M 40.601 22.687 C 37.501 23.187 34.801 23.987 32.301 25.287 C 34.401 26.487 37.601 26.487 40.201 25.487 L 40.601 22.687 Z" fill="white"/>
      <rect x="23.799" y="23.692" width="3.297" height="3.182" rx="0.872" ry="0.872" fill="white"/>
      <rect x="27.518" y="23.732" width="3.152" height="3.103" rx="0.773" ry="0.773" fill="white"/>
      <rect x="20.878" y="27.694" width="12.828" height="1.781" rx="2.657" ry="2.657" fill="white"/>
    </svg>
  `;
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
};

const StableTrackCursorPlacemark: React.FC<{
  point: any;
  iconHref: string;
  balloonHeader: string;
  balloonBody: string;
  hintContent: string;
}> = React.memo(({ point, iconHref, balloonHeader, balloonBody, hintContent }) => {
  const placemarkRef = React.useRef<any>(null);
  const initialCoordinateRef = React.useRef<[number, number]>(point.coordinate);
  const initialPropertiesRef = React.useRef({
    hintContent,
    balloonContentHeader: balloonHeader,
    balloonContentBody: balloonBody,
  });
  const options = React.useMemo(() => ({
    iconLayout: 'default#image',
    iconImageHref: iconHref,
    iconImageSize: [34, 46],
    iconImageOffset: [-17, -46],
    zIndex: 1580,
  }), [iconHref]);

  React.useEffect(() => {
    const placemark = placemarkRef.current;
    if (!placemark) return;
    placemark.geometry?.setCoordinates?.(point.coordinate);
    placemark.properties?.set?.({
      hintContent,
      balloonContentHeader: balloonHeader,
      balloonContentBody: balloonBody,
    });
  }, [point.coordinate, hintContent, balloonHeader, balloonBody]);

  React.useEffect(() => {
    placemarkRef.current?.options?.set?.('iconImageHref', iconHref);
  }, [iconHref]);

  return (
    <Placemark
      instanceRef={placemarkRef}
      geometry={initialCoordinateRef.current}
      properties={initialPropertiesRef.current}
      options={options}
      modules={['geoObject.addon.balloon', 'geoObject.addon.hint']}
    />
  );
});

const createTrackArrowMarker = (color: string, bearing: number): string => {
  const svg = `
    <svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
      <g transform="rotate(${bearing} 14 14)">
        <path d="M14 3 L22 21 L14 17 L6 21 Z"
              fill="${color}" stroke="white" stroke-width="2" stroke-linejoin="round"/>
      </g>
    </svg>
  `;
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
};

const createTrackEndpointMarker = (color: string, type: 'start' | 'finish'): string => {
  const label = type === 'start' ? 'START' : 'FINISH';
  const svg = `
    <svg width="120" height="160" viewBox="0 0 120 160" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="-20%" y="-10%" width="140%" height="140%">
          <feDropShadow dx="0" dy="5" stdDeviation="5" flood-color="#0f172a" flood-opacity="0.28"/>
        </filter>
      </defs>
      <path d="M60 10
               C30 10, 10 30, 10 60
               C10 100, 60 150, 60 150
               C60 150, 110 100, 110 60
               C110 30, 90 10, 60 10 Z"
            fill="${color}" stroke="white" stroke-width="6" filter="url(#shadow)"/>
      <circle cx="60" cy="60" r="25" fill="#ffffff"/>
      <text x="60" y="65"
            font-size="${type === 'start' ? 12 : 11}"
            text-anchor="middle"
            fill="${color}"
            font-family="Arial, sans-serif"
            font-weight="bold">
        ${label}
      </text>
    </svg>
  `;
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
};

// Функция создания темы с поддержкой светлой/темной версии
const createAppTheme = (mode: 'light' | 'dark') => createTheme({
  palette: {
    mode,
    primary: {
      main: '#4f46e5',
      light: '#818cf8',
      dark: '#3730a3',
    },
    secondary: {
      main: '#06b6d4',
      light: '#67e8f9',
      dark: '#0891b2',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#047857',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    background: mode === 'light' ? {
      default: '#f8fafc',
      paper: '#ffffff',
    } : {
      default: '#0f172a',
      paper: '#1e293b',
    },
    grey: mode === 'light' ? {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    } : {
      50: '#0f172a',
      100: '#1e293b',
      200: '#334155',
      300: '#475569',
      400: '#64748b',
      500: '#94a3b8',
      600: '#cbd5e1',
      700: '#e2e8f0',
      800: '#f1f5f9',
      900: '#f8fafc',
    },
  },
  typography: {
    fontSize: 14,
    // Увеличенные размеры для мобильных
    h1: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    button: {
      fontSize: '1rem',
      fontWeight: 500,
    },
  },
  components: {
    // Современные кнопки с градиентами и анимациями
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: 44,
          padding: '12px 24px',
          fontSize: '1rem',
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
          boxShadow: '0 4px 15px rgba(79, 70, 229, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #3730a3 0%, #6d28d9 100%)',
            boxShadow: '0 8px 25px rgba(79, 70, 229, 0.4)',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            backgroundColor: 'rgba(79, 70, 229, 0.04)',
          },
        },
        sizeLarge: {
          minHeight: 56,
          padding: '16px 32px',
          fontSize: '1.1rem',
        },
      },
    },
    // Современные карточки с улучшенными тенями
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(226, 232, 240, 0.8)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    // Современные поля ввода
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#4f46e5',
              },
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: 2,
                borderColor: '#4f46e5',
                boxShadow: '0 0 0 3px rgba(79, 70, 229, 0.1)',
              },
            },
          },
          '& .MuiInputBase-root': {
            minHeight: 48,
          },
        },
      },
    },
    // Современные табы
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-indicator': {
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            height: 3,
            borderRadius: 3,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1rem',
          minHeight: 64,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(79, 70, 229, 0.04)',
          },
          '&.Mui-selected': {
            background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
          },
        },
      },
    },
    // Современные чипы
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
        colorSuccess: {
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
        },
        colorWarning: {
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: 'white',
        },
        colorError: {
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: 'white',
        },
      },
    },
    // Увеличенные чекбоксы и радио кнопки
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 12,
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: 12,
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});


// Функция для получения цвета маркера по индексу маршрута
const getRouteColor = (index: number): string => {
  const colors = [
    'islands#blueIcon',
    'islands#redIcon',
    'islands#greenIcon',
    'islands#yellowIcon',
    'islands#brownIcon',
    'islands#violetIcon',
    'islands#grayIcon',
    'islands#lightBlueIcon',
    'islands#orangeIcon',
    'islands#darkGreenIcon',
    'islands#pinkIcon',
    'islands#darkOrangeIcon'
  ];

  // Используем индекс маршрута в списке для выбора цвета
  return colors[index % colors.length];
};

// Функция для преобразования цвета Yandex Maps в CSS цвет
const getRouteColorCSS = (index: number): string => {
  const yandexColor = getRouteColor(index);
  const colorMap: Record<string, string> = {
    'islands#blueIcon': '#2196F3',
    'islands#redIcon': '#F44336',
    'islands#greenIcon': '#4CAF50',
    'islands#yellowIcon': '#FFEB3B',
    'islands#darkOrangeIcon': '#FF5722',
    'islands#violetIcon': '#9C27B0',
    'islands#pinkIcon': '#E91E63',
    'islands#darkGreenIcon': '#388E3C',
    'islands#orangeIcon': '#FF9800',
    'islands#brownIcon': '#795548',
    'islands#grayIcon': '#9E9E9E',
    'islands#lightBlueIcon': '#03A9F4'
  };
  return colorMap[yandexColor] || '#2196F3';
};

// Функция для создания URL маршрута в Яндекс.Картах
const createYandexRouteURL = (route: any): string => {
  // Фильтруем доставки с координатами
  const deliveriesWithCoords = route.deliveries?.filter((delivery: any) =>
    delivery.location?.latitude && delivery.location?.longitude
  ) || [];

  if (deliveriesWithCoords.length === 0) return '';

  // Формируем точки маршрута для параметра rtext
  const points = deliveriesWithCoords.map((delivery: any) =>
    `${delivery.location.latitude},${delivery.location.longitude}`
  );

  // Яндекс.Карты поддерживают до 25 точек в маршруте
  const limitedPoints = points.slice(0, 25);

  // Создаем URL с маршрутом
  const baseURL = 'https://yandex.ru/maps/';
  const routeParam = `rtext=${limitedPoints.join('~')}`;
  const typeParam = 'rtt=auto'; // тип маршрута - автомобильный
  const mapParam = 'l=map'; // тип карты

  return `${baseURL}?${routeParam}&${typeParam}&${mapParam}`;
};

const formatIgnitionStatus = (value: any): string => {
  if (value === undefined || value === null || value === '') return 'не указано';
  const normalized = String(value).trim().toLowerCase();
  if (['1', 'true', 'on', 'yes', 'вкл', 'включено'].includes(normalized)) return 'включено';
  if (['0', 'false', 'off', 'no', 'выкл', 'выключено'].includes(normalized)) return 'выключено';
  return String(value);
};

const formatTemperatureValue = (value: any): string => {
  if (value === undefined || value === null || value === '') return 'не указана';
  if (Array.isArray(value)) {
    const items = value
      .map((item) => {
        if (item && typeof item === 'object') {
          const rawValue = item.value ?? item.temperature ?? item.temp;
          const name = item.name ? `${item.name}: ` : '';
          return rawValue !== undefined && rawValue !== null && rawValue !== ''
            ? `${name}${formatTemperatureValue(rawValue)}`
            : '';
        }
        return formatTemperatureValue(item);
      })
      .filter(Boolean);
    return items.length ? items.join('<br/>') : 'не указана';
  }
  if (typeof value === 'object') {
    const rawValue = value.value ?? value.temperature ?? value.temp;
    if (rawValue !== undefined && rawValue !== null && rawValue !== '') {
      const name = value.name ? `${value.name}: ` : '';
      return `${name}${formatTemperatureValue(rawValue)}`;
    }
    return 'не указана';
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return `${numeric.toFixed(1).replace(/\.0$/, '')} °C`;
  return String(value);
};

const getRouteTotalPackages = (route: any): number => {
  return (route?.deliveries || []).reduce((sum: number, delivery: any) => {
    if (!delivery?.packages) return sum;
    const numbers: string[] = String(delivery.packages).match(/\d+/g) || [];
    return sum + numbers.reduce((packageSum: number, value: string) => packageSum + parseInt(value, 10), 0);
  }, 0);
};

const formatLoadingTime = (route: any): string => {
  if (!route?.loading_start) return 'не указано';
  const start = String(route.loading_start).replace('T', ' ').substring(11, 16);
  const stop = route.loading_stop ? String(route.loading_stop).replace('T', ' ').substring(11, 16) : '';
  return stop ? `${start} - ${stop}` : start;
};

const formatRouteWeight = (route: any): string => {
  const weight = Number(route?.weight_kg);
  if (!Number.isFinite(weight)) return '0 кг';
  return `${weight.toFixed(3)} кг`;
};

const formatRouteDistance = (route: any): string => {
  const distance = Number(route?.distance);
  if (!Number.isFinite(distance)) return '0 км';
  return `${distance} км`;
};

const formatActualDistance = (value: any): string => {
  const distance = Number(value);
  if (!Number.isFinite(distance)) return 'не указано';
  return `${distance.toFixed(1).replace(/\.0$/, '')} км`;
};

// Компонент карты
const DeliveryMap: React.FC<{
  routes: any[];
  allRoutes?: any[];
  clientFilter: string;
  activeDeliveryPoint?: {
    routeNumber: string;
    invoiceNumber: string;
    deliveryId: string;
    coordinates?: [number, number];
  } | null;
  mapLoading?: boolean;
  setMapLoading?: (loading: boolean) => void;
  darkMode?: boolean;
  vehiclePositions?: any[];
  selectedTrack?: any | null;
  onShowRouteTrack?: (route: any, color: string) => void;
}> = ({ routes, allRoutes, clientFilter, activeDeliveryPoint, mapLoading = true, setMapLoading, darkMode = false, vehiclePositions = [], selectedTrack = null, onShowRouteTrack }) => {
  const mapShellRef = React.useRef<HTMLDivElement | null>(null);
  const mapRef = React.useRef<any>(null);
  const [isMapFullscreen, setIsMapFullscreen] = React.useState(false);
  const trackPoints = React.useMemo(() => {
    const validPoints = (selectedTrack?.points || [])
      .map((point: any) => ({
        ...point,
        coordinate: [Number(point.lat), Number(point.lon)] as [number, number],
      }))
      .filter((point: any) => Number.isFinite(point.coordinate[0]) && Number.isFinite(point.coordinate[1]));
    const firstMovingIndex = validPoints.findIndex(isMovingTrackPoint);
    const lastMovingIndexFromEnd = [...validPoints].reverse().findIndex(isMovingTrackPoint);
    if (firstMovingIndex === -1 || lastMovingIndexFromEnd === -1) {
      return validPoints;
    }
    const lastMovingIndex = validPoints.length - 1 - lastMovingIndexFromEnd;
    return validPoints.slice(firstMovingIndex, lastMovingIndex + 1);
  }, [selectedTrack]);
  const trackCoordinates = React.useMemo(() => {
    return trackPoints.map((point: any) => point.coordinate);
  }, [trackPoints]);
  const [trackCursorIndex, setTrackCursorIndex] = React.useState(0);
  const [trackPlaying, setTrackPlaying] = React.useState(false);
  const trackColor = selectedTrack?.color || '#0284c7';
  const currentTrackIndex = Math.min(trackCursorIndex, Math.max(trackPoints.length - 1, 0));
  const currentTrackPoint = trackPoints[currentTrackIndex];
  const shouldShowTrackCursor = trackPlaying || currentTrackIndex < Math.max(trackPoints.length - 1, 0);
  const hasLiveVehicleForSelectedTrack = React.useMemo(() => {
    if (!selectedTrack?.routeNumber) return false;
    return vehiclePositions.some((vehicle) => String(vehicle.routeNumber) === String(selectedTrack.routeNumber));
  }, [selectedTrack?.routeNumber, vehiclePositions]);
  const routeLookup = React.useMemo(() => {
    const lookup = new globalThis.Map<string, { route: any; color: string }>();
    const routeList = allRoutes || routes;
    routeList.forEach((route: any, index: number) => {
      lookup.set(String(route.routeNumber), {
        route,
        color: getRouteColorCSS(index),
      });
    });
    return lookup;
  }, [routes, allRoutes]);
  const playedTrackCoordinates = React.useMemo(() => {
    if (trackCoordinates.length < 2) return [];
    return trackCoordinates.slice(0, currentTrackIndex + 1);
  }, [trackCoordinates, currentTrackIndex]);
  const currentTrackMarkerIcon = React.useMemo(
    () => createVehicleMarker(trackColor, hasLiveVehicleForSelectedTrack ? 0.48 : 1),
    [trackColor, hasLiveVehicleForSelectedTrack]
  );
  const currentTrackSpeed = currentTrackPoint?.speed !== undefined && currentTrackPoint?.speed !== null
    ? `${Math.round(Number(currentTrackPoint.speed) || 0)} км/ч`
    : '0 км/ч';

  React.useEffect(() => {
    setTrackCursorIndex(Math.max(trackPoints.length - 1, 0));
    setTrackPlaying(false);
  }, [selectedTrack, trackPoints.length]);

  React.useEffect(() => {
    setTrackCursorIndex((prev) => Math.min(prev, Math.max(trackPoints.length - 1, 0)));
    if (trackPoints.length < 2) {
      setTrackPlaying(false);
    }
  }, [trackPoints.length]);

  React.useEffect(() => {
    if (!trackPlaying || trackPoints.length < 2) return undefined;

    const timer = window.setInterval(() => {
      setTrackCursorIndex((prev) => {
        if (prev >= trackPoints.length - 1) {
          setTrackPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, 180);

    return () => window.clearInterval(timer);
  }, [trackPlaying, trackPoints.length]);

  React.useEffect(() => {
    if (trackPoints.length < 2) return undefined;

    const handleSpacePlayback = (event: KeyboardEvent) => {
      if (event.code !== 'Space') return;

      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      if (tagName === 'input' || tagName === 'textarea' || tagName === 'select' || target?.isContentEditable) {
        return;
      }

      event.preventDefault();
      if (!trackPlaying && trackCursorIndex >= trackPoints.length - 1) {
        setTrackCursorIndex(0);
      }
      setTrackPlaying((prev) => !prev);
    };

    window.addEventListener('keydown', handleSpacePlayback);
    return () => window.removeEventListener('keydown', handleSpacePlayback);
  }, [trackCursorIndex, trackPlaying, trackPoints.length]);

  const getTrackPointBalloonBody = React.useCallback((point: any, extra = ''): string => {
    const speed = point?.speed !== undefined && point?.speed !== null
      ? `${Math.round(Number(point.speed) || 0)} км/ч`
      : 'не указана';
    return `
      <div style="font-family: Arial, sans-serif; line-height: 1.45;">
        <div><strong>Время:</strong> ${formatTrackPointTime(point?.dt)}</div>
        <div><strong>Скорость:</strong> ${speed}</div>
        ${extra}
      </div>
    `;
  }, []);
  const trackDirectionMarkers = React.useMemo(() => {
    if (trackPoints.length < 2) return [];

    let totalDistance = 0;
    for (let i = 1; i < trackPoints.length; i += 1) {
      totalDistance += getPointDistanceMeters(trackPoints[i - 1].coordinate, trackPoints[i].coordinate);
    }

    const targetMarkers = Math.min(36, Math.max(5, Math.floor(totalDistance / 1200)));
    const spacing = Math.max(450, totalDistance / targetMarkers);
    const markers: Array<{ coordinate: [number, number]; bearing: number; key: string; point: any }> = [];
    let distanceSinceMarker = spacing * 0.5;

    for (let i = 1; i < trackPoints.length; i += 1) {
      const prev = trackPoints[i - 1].coordinate;
      const current = trackPoints[i].coordinate;
      const segmentDistance = getPointDistanceMeters(prev, current);
      if (segmentDistance < 15) continue;

      distanceSinceMarker += segmentDistance;
      if (distanceSinceMarker >= spacing) {
        markers.push({
          coordinate: current,
          bearing: getBearingDegrees(prev, current),
          key: `${i}-${current[0]}-${current[1]}`,
          point: trackPoints[i],
        });
        distanceSinceMarker = 0;
      }
    }

    return markers;
  }, [trackPoints]);

  const handleTrackLineClick = React.useCallback((event: any) => {
    if (!trackPoints.length) return;
    const coords = event?.get?.('coords');
    if (!coords || coords.length < 2) return;
    const clicked: [number, number] = [Number(coords[0]), Number(coords[1])];
    if (!Number.isFinite(clicked[0]) || !Number.isFinite(clicked[1])) return;

    let nearest = trackPoints[0];
    let nearestDistance = getPointDistanceMeters(clicked, nearest.coordinate);
    for (const point of trackPoints) {
      const distance = getPointDistanceMeters(clicked, point.coordinate);
      if (distance < nearestDistance) {
        nearest = point;
        nearestDistance = distance;
      }
    }

    const content = getTrackPointBalloonBody(
      nearest,
      `<div style="font-size: 12px; color: ${darkMode ? '#cbd5e1' : '#64748b'};">Ближайшая точка: ${Math.round(nearestDistance)} м</div>`
    );

    mapRef.current?.balloon?.open(clicked, {
      contentHeader: `Трек: ${selectedTrack?.transport || selectedTrack?.vehicle?.transport || ''}`,
      contentBody: content,
    });
  }, [trackPoints, darkMode, selectedTrack, getTrackPointBalloonBody]);

  const trackBaseLine = React.useMemo(() => {
    if (trackCoordinates.length <= 1) return null;

    return (
      <Polyline
        geometry={trackCoordinates}
        properties={{
          hintContent: 'Нажмите на трек, чтобы увидеть время',
        }}
          options={{
            strokeColor: trackColor,
            strokeWidth: 4,
            strokeOpacity: 0.28,
            strokeStyle: 'dash',
            zIndex: 1200,
        }}
        onClick={handleTrackLineClick}
        modules={['geoObject.addon.hint']}
      />
    );
  }, [trackCoordinates, trackColor, handleTrackLineClick]);

  const trackStaticMarkers = React.useMemo(() => {
    if (trackCoordinates.length <= 1) return null;

    return (
      <>
        <Placemark
          key="track-start"
          geometry={trackCoordinates[0]}
          properties={{
            hintContent: `Старт: ${formatTrackPointTime(trackPoints[0]?.dt)}`,
            balloonContentHeader: 'Старт трека',
            balloonContentBody: getTrackPointBalloonBody(trackPoints[0]),
          }}
          options={{
            iconLayout: 'default#image',
            iconImageHref: createTrackEndpointMarker('#16a34a', 'start'),
            iconImageSize: [24, 30],
            iconImageOffset: [-12, -30],
            zIndex: 1300,
          }}
          modules={['geoObject.addon.balloon', 'geoObject.addon.hint']}
        />
        <Placemark
          key="track-finish"
          geometry={trackCoordinates[trackCoordinates.length - 1]}
          properties={{
            hintContent: `Финиш: ${formatTrackPointTime(trackPoints[trackPoints.length - 1]?.dt)}`,
            balloonContentHeader: 'Финиш трека',
            balloonContentBody: getTrackPointBalloonBody(trackPoints[trackPoints.length - 1]),
          }}
          options={{
            iconLayout: 'default#image',
            iconImageHref: createTrackEndpointMarker('#dc2626', 'finish'),
            iconImageSize: [24, 30],
            iconImageOffset: [-12, -30],
            zIndex: 1301,
          }}
          modules={['geoObject.addon.balloon', 'geoObject.addon.hint']}
        />
        {trackDirectionMarkers.map((marker) => (
          <Placemark
            key={`track-arrow-${marker.key}`}
            geometry={marker.coordinate}
            properties={{
              hintContent: `Время: ${formatTrackPointTime(marker.point.dt)}`,
              balloonContentHeader: 'Направление движения',
              balloonContentBody: getTrackPointBalloonBody(marker.point),
            }}
            options={{
              iconLayout: 'default#image',
              iconImageHref: createTrackArrowMarker(trackColor, marker.bearing),
              iconImageSize: [18, 18],
              iconImageOffset: [-9, -9],
              opacity: 0.72,
              zIndex: 1250,
              interactiveZIndex: false,
            }}
            modules={['geoObject.addon.balloon', 'geoObject.addon.hint']}
          />
        ))}
      </>
    );
  }, [trackCoordinates, trackPoints, trackDirectionMarkers, trackColor, getTrackPointBalloonBody]);

  // State для управления автозумом
  const [autoZoom, setAutoZoom] = React.useState<boolean>(() => {
    const saved = localStorage.getItem('mapAutoZoom');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Сохраняем настройку автозума в localStorage
  React.useEffect(() => {
    localStorage.setItem('mapAutoZoom', JSON.stringify(autoZoom));
  }, [autoZoom]);

  React.useEffect(() => {
    const handleTrackButtonClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const button = target?.closest?.('[data-track-route-number]') as HTMLElement | null;
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      const routeNumber = button.dataset.trackRouteNumber;
      const routeData = routeNumber ? routeLookup.get(routeNumber) : null;
      if (!routeData || !onShowRouteTrack) return;

      onShowRouteTrack(routeData.route, routeData.color);
      try {
        mapRef.current?.balloon?.close?.();
      } catch (error) {
        console.warn('Не удалось закрыть балун после запуска трека:', error);
      }
    };

    document.addEventListener('click', handleTrackButtonClick);
    return () => document.removeEventListener('click', handleTrackButtonClick);
  }, [routeLookup, onShowRouteTrack]);

  React.useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement = document.fullscreenElement || (document as any).webkitFullscreenElement;
      setIsMapFullscreen(fullscreenElement === mapShellRef.current);
      window.setTimeout(() => {
        try {
          mapRef.current?.container?.fitToViewport?.();
        } catch (error) {
          console.warn('Не удалось обновить размер карты после fullscreen:', error);
        }
      }, 120);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange as EventListener);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange as EventListener);
    };
  }, []);

  const toggleMapFullscreen = React.useCallback(() => {
    const shell = mapShellRef.current;
    if (!shell) return;

    const fullscreenElement = document.fullscreenElement || (document as any).webkitFullscreenElement;
    if (fullscreenElement === shell) {
      const exitFullscreen = document.exitFullscreen || (document as any).webkitExitFullscreen;
      exitFullscreen?.call(document);
      return;
    }

    const requestFullscreen = shell.requestFullscreen || (shell as any).webkitRequestFullscreen;
    requestFullscreen?.call(shell);
  }, []);

  // Центрируем карту на активной точке
  React.useEffect(() => {
    if (activeDeliveryPoint?.coordinates && mapRef.current) {
      try {
        // mapRef.current содержит экземпляр карты Яндекса
        if (autoZoom) {
          // С автозумом - меняем центр и зум
          mapRef.current.setCenter(activeDeliveryPoint.coordinates, 15, {
            duration: 500
          });
        } else {
          // Без автозума - только центрируем
          mapRef.current.setCenter(activeDeliveryPoint.coordinates, undefined, {
            duration: 500
          });
        }
      } catch (error) {
        console.warn('Не удалось центрировать карту:', error);
        console.log('mapRef.current:', mapRef.current);
        console.log('Доступные методы:', Object.getOwnPropertyNames(mapRef.current));
      }
    }
  }, [activeDeliveryPoint, autoZoom]);

  // Получаем все точки доставки с координатами
  const deliveryPoints = React.useMemo(() => {
    // Используем allRoutes для определения индекса, если передан
    const routeList = allRoutes || routes;

    return routes.flatMap((route) => {
      // Находим индекс маршрута в полном отсортированном списке
      const routeIndex = routeList.findIndex(r => r.routeNumber === route.routeNumber);

      return (route.deliveries || [])
        .filter((delivery: any) =>
          delivery.location?.latitude &&
          delivery.location?.longitude &&
          (!clientFilter.trim() || delivery.client?.toLowerCase().includes(clientFilter.toLowerCase()))
        )
        .map((delivery: any) => ({
          ...delivery,
          routeNumber: route.routeNumber,
          routeStatus: route.routeStatus,
          driver: route.driver,
          routeIndex: routeIndex
        }));
    });
  }, [routes, allRoutes, clientFilter]);

  const vehicleMarkers = React.useMemo(() => {
    return vehiclePositions.map((vehicle, index) => {
      const speed = Number.isFinite(Number(vehicle.speed))
        ? Math.round(Number(vehicle.speed))
        : 0;
      const dt = vehicle.dt || 'не указано';
      const freshness = vehicle.stale ? '⚠ Позиция устарела — последнее известное местоположение' : 'Актуальная позиция';
      const address = vehicle.address || 'Адрес не определен';
      const ignitionStatus = formatIgnitionStatus(vehicle.ignition);
      const temperatureText = formatTemperatureValue(vehicle.temperature);
      const routeList = allRoutes || routes;
      const routeIndex = routeList.findIndex(r => String(r.routeNumber) === String(vehicle.routeNumber));
      const vehicleRoute = routeIndex >= 0 ? routeList[routeIndex] : null;
      const routeColor = getRouteColorCSS(routeIndex >= 0 ? routeIndex : index);
      const routeNumberForTrack = String(vehicle.routeNumber || '').replace(/"/g, '&quot;');
      const mutedTextColor = darkMode ? '#cbd5e1' : '#475569';
      const labelColor = darkMode ? '#38bdf8' : '#0284c7';
      const loadingTime = formatLoadingTime(vehicleRoute);
      const routeWeight = formatRouteWeight(vehicleRoute);
      const routePackages = getRouteTotalPackages(vehicleRoute);
      const plannedDistance = formatRouteDistance(vehicleRoute);
      const actualDistance = formatActualDistance(vehicle.actualDistanceKm);
      const trackButton = onShowRouteTrack && routeNumberForTrack ? `
                <div style="margin: 10px 0 8px;">
                  <button
                    type="button"
                    data-track-route-number="${routeNumberForTrack}"
                    style="display: inline-flex; align-items: center; gap: 6px; border: 1px solid ${routeColor}; background: ${routeColor}; color: #ffffff; border-radius: 8px; padding: 7px 10px; font-size: 12px; font-weight: 700; cursor: pointer; line-height: 1;"
                  >
                    ↱ Показать трек
                  </button>
                </div>
      ` : '';
      return (
        <Placemark
          key={`vehicle-${vehicle.unitId || index}`}
          geometry={[vehicle.lat, vehicle.lon]}
          properties={{
            balloonContentHeader: `${vehicle.stale ? '⚠ Устаревшая позиция' : '🚚'} ${vehicle.transport || vehicle.vehicleName || 'Машина'}`,
            balloonContentBody: `
              <div style="font-family: Arial, sans-serif; line-height: 1.45; max-width: 320px; color: ${darkMode ? '#e2e8f0' : '#1f2937'};">
                <div style="margin-bottom: 8px; color: ${vehicle.stale ? '#b45309' : labelColor};">${freshness}</div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: ${labelColor};">Маршрут:</strong><br/>
                  <span>№${vehicle.routeNumber || 'не указан'}</span>
                  <span style="font-size: 12px; color: ${mutedTextColor};">
                    &nbsp;· Погрузка: ${loadingTime}<br/>
                    Вес: ${routeWeight} · Мест: ${routePackages}<br/>
                    Дистанция план: ${plannedDistance} · Дистанция факт: ${actualDistance}
                  </span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: ${labelColor};">Водитель:</strong><br/>
                  <span>${vehicle.driver || 'не указан'}</span>
                </div>
                <div style="margin-bottom: 8px; display: flex; gap: 16px; flex-wrap: wrap;">
                  <div>
                    <strong style="color: ${labelColor};">Скорость:</strong><br/>
                    <span>${speed} км/ч</span>
                  </div>
                  <div>
                    <strong style="color: ${labelColor};">Время:</strong><br/>
                    <span>${dt}</span>
                  </div>
                </div>
                <div style="margin-bottom: 8px; display: flex; gap: 16px; flex-wrap: wrap;">
                  <div>
                    <strong style="color: ${labelColor};">Зажигание:</strong><br/>
                    <span>${ignitionStatus}</span>
                  </div>
                  <div>
                    <strong style="color: ${labelColor};">Температура:</strong><br/>
                    <span>${temperatureText}</span>
                  </div>
                </div>
                ${trackButton}
                <div>
                  <strong style="color: ${labelColor};">Адрес:</strong><br/>
                  <span style="font-size: 12px; color: ${mutedTextColor};">${address}</span>
                </div>
              </div>
            `,
          }}
          options={{
            iconLayout: 'default#image',
            iconImageHref: createVehicleMarker(routeColor, vehicle.stale ? 0.35 : 1),
            iconImageSize: [38, 52],
            iconImageOffset: [-19, -52],
            openBalloonOnClick: true,
            hideIconOnBalloonOpen: false,
            zIndex: 2000,
          }}
          modules={['geoObject.addon.balloon']}
        />
      );
    });
  }, [vehiclePositions, allRoutes, routes, darkMode, onShowRouteTrack]);

  const deliveryMarkers = React.useMemo(() => {
    return deliveryPoints.map((delivery, index) => {
      const isActive = activeDeliveryPoint &&
        activeDeliveryPoint.routeNumber === delivery.routeNumber &&
        activeDeliveryPoint.invoiceNumber === delivery.invoiceNumber &&
        activeDeliveryPoint.deliveryId === delivery.id;

      const getActualTime = (report) => {
        if (!report) return 'Не указано';
        const match = report.match(/в (\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}:\d{2})/);
        return match ? match[1].split(' ')[1] : 'Не указано';
      };

      const getPlanTime = (estimatedTime) => {
        if (!estimatedTime || estimatedTime === '0001-01-01T09:29:18') return 'Не указано';
        try {
          return new Date(estimatedTime).toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'});
        } catch {
          return 'Не указано';
        }
      };

      const deliveryStatus = getDeliveryStatusText(delivery);
      const statusColor = getDeliveryStatusColor(delivery);
      const statusText = `${deliveryStatus.emoji} ${deliveryStatus.text}`;
      const clientName = delivery.client || 'Не указан';
      const driverName = delivery.driver || 'Не указан';
      const planTime = getPlanTime(delivery.estimated_time);
      const actualTime = getActualTime(delivery.deliveryReport);
      const weight = delivery.weight || 0;
      const packages = delivery.packages || 'Не указано';
      const invoiceNumber = delivery.invoiceNumber || 'Не указана';
      const description = delivery.deliveryReport ? delivery.deliveryReport.replace(/\n/g, '<br/>').trim() : 'Нет описания';
      const mutedTextColor = darkMode ? '#cbd5e1' : '#475569';
      const outOfRouteBadge = delivery.outOfRoute ? `
                <div style="margin-bottom: 8px;">
                  <span style="display: inline-block; padding: 3px 8px; border-radius: 999px; background: ${darkMode ? 'rgba(251, 191, 36, 0.14)' : '#fff7ed'}; color: ${darkMode ? '#fbbf24' : '#c2410c'}; border: 1px solid ${darkMode ? 'rgba(251, 191, 36, 0.35)' : '#fed7aa'}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0;">
                    Вне маршрута
                  </span>
                </div>
                <div style="margin-bottom: 8px; font-size: 12px; color: ${mutedTextColor};">
                  ${delivery.outOfRouteReason || 'Точка сохранена по отчету водителя, но сейчас отсутствует в актуальном маршруте 1С.'}
                </div>
      ` : '';
      const routeColorHex = isActive ? '#212121' : getRouteColorCSS(delivery.routeIndex);
      const deliveryStatusColor = getDeliveryStatusColor(delivery);
      const routeNumberForTrack = String(delivery.routeNumber || '').replace(/"/g, '&quot;');
      const trackButton = onShowRouteTrack ? `
                <div style="margin: 10px 0 8px;">
                  <button
                    type="button"
                    data-track-route-number="${routeNumberForTrack}"
                    style="display: inline-flex; align-items: center; gap: 6px; border: 1px solid ${routeColorHex}; background: ${routeColorHex}; color: #ffffff; border-radius: 8px; padding: 7px 10px; font-size: 12px; font-weight: 700; cursor: pointer; line-height: 1;"
                  >
                    ↱ Показать трек
                  </button>
                </div>
      ` : '';
      const customMarkerIcon = createMarkerWithBadge(
        delivery.id || '?',
        routeColorHex,
        deliveryStatusColor,
        delivery
      );

      return (
        <Placemark
          key={`${delivery.routeNumber}-${delivery.invoiceNumber}-${index}`}
          geometry={[
            parseFloat(delivery.location.latitude),
            parseFloat(delivery.location.longitude)
          ]}
          properties={{
            balloonContentHeader: `Точка доставки №${delivery.id}`,
            balloonContentBody: `
              <div style="font-family: Arial, sans-serif; line-height: 1.4; max-width: 350px; color: ${darkMode ? '#e2e8f0' : '#1f2937'};">
                ${outOfRouteBadge}
                <div style="margin-bottom: 8px;">
                  <strong style="color: #1976d2;">Клиент:</strong><br/>
                  <span>${clientName}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #1976d2;">Водитель:</strong><br/>
                  <span>${driverName}</span>
                </div>

                <div style="margin-bottom: 8px; display: flex; gap: 16px; flex-wrap: wrap;">
                  <div>
                    <strong style="color: #1976d2;">Маршрут:</strong><br/>
                    <span>№${delivery.routeNumber}</span>
                  </div>
                  <div>
                    <strong style="color: #1976d2;">Накладная:</strong><br/>
                    <span>${invoiceNumber}</span>
                  </div>
                  <div>
                    <strong style="color: #1976d2;">Статус:</strong><br/>
                    <span style="color: ${statusColor};">${statusText}</span>
                  </div>
                </div>
                ${trackButton}

                <div style="margin-bottom: 8px; display: flex; gap: 16px; flex-wrap: wrap;">
                  <div>
                    <strong style="color: #1976d2;">Время план/факт:</strong><br/>
                    <span>План: ${planTime}</span><br/>
                    <span>Факт: ${actualTime}</span>
                  </div>
                  <div>
                    <strong style="color: #1976d2;">Вес/Упаковки:</strong><br/>
                    <span>⚖️ ${weight} кг</span><br/>
                    <span>📦 ${packages}</span>
                  </div>
                </div>

                <div style="margin-bottom: 8px;">
                  <strong style="color: #1976d2;">Описание:</strong><br/>
                  <span style="font-size: 12px; color: ${mutedTextColor};">
                    ${description}
                  </span>
                </div>
              </div>
            `,
            balloonContentFooter: ``
          }}
          options={{
            iconLayout: 'default#image',
            iconImageHref: customMarkerIcon,
            iconImageSize: [26, 32],
            iconImageOffset: [-13, -32],
            openBalloonOnClick: true,
            hideIconOnBalloonOpen: false,
            zIndex: isActive ? 1000 : 100
          }}
          modules={['geoObject.addon.balloon']}
        />
      );
    });
  }, [deliveryPoints, activeDeliveryPoint, darkMode, onShowRouteTrack]);

  const warehouseMarker = React.useMemo(() => (
    <Placemark
      geometry={[42.883564, 47.618345]}
      properties={{
        balloonContentHeader: '🏢 Склад',
        balloonContentBody: `
          <div style="font-family: Arial, sans-serif; line-height: 1.4;">
            <strong>Главный склад</strong><br/>
            <span style="color: #666; font-size: 12px;">Точка отправки всех маршрутов</span>
          </div>
        `
      }}
      options={{
        iconLayout: 'default#image',
        iconImageHref: createWarehouseMarker(),
        iconImageSize: [30, 36],
        iconImageOffset: [-15, -36],
        openBalloonOnClick: true,
        hideIconOnBalloonOpen: false
      }}
      modules={['geoObject.addon.balloon']}
    />
  ), []);

  // Центр карты (Махачкала)
  const mapCenter = [42.9750, 47.5050];

  return (
    <Box
      ref={mapShellRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor: darkMode ? '#0f172a' : '#f8fafc',
        '&:fullscreen': {
          height: '100vh',
        },
        '&:-webkit-full-screen': {
          height: '100vh',
        },
      }}
    >
      {/* Тумблер автозума */}
      <Box sx={{
        position: 'absolute',
        top: 10,
        left: 10,
        zIndex: 1000,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        padding: '6px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
          Auto Zoom
        </Typography>
        <Switch
          checked={autoZoom}
          onChange={(e) => setAutoZoom(e.target.checked)}
          size="small"
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: 'primary.main',
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: 'primary.main',
            }
          }}
        />
      </Box>

      <IconButton
        size="small"
        title={isMapFullscreen ? 'Выйти из полного экрана' : 'Полный экран'}
        onClick={toggleMapFullscreen}
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 1000,
          width: 38,
          height: 38,
          backgroundColor: 'background.paper',
          color: 'text.primary',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          '&:hover': {
            backgroundColor: 'background.paper',
            filter: 'brightness(0.96)',
          },
        }}
      >
        {isMapFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
      </IconButton>

      {mapLoading && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.05) 0%, rgba(6, 182, 212, 0.05) 100%)',
          backdropFilter: 'blur(4px)',
          zIndex: 1000,
          borderRadius: 2,
        }}>
          <Box sx={{ position: 'relative', mb: 3 }}>
            <CircularProgress
              size={48}
              thickness={4}
              sx={{
                color: 'primary.main',
                animationDuration: '1.2s',
                position: 'absolute',
                left: 0,
              }}
            />
            <CircularProgress
              size={48}
              thickness={4}
              variant="determinate"
              value={100}
              sx={{
                color: 'rgba(79, 70, 229, 0.1)',
                transform: 'rotate(-90deg)!important',
              }}
            />
          </Box>
          <Typography
            variant="h6"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: 'primary.main',
              textAlign: 'center'
            }}
          >
            Загрузка карты
          </Typography>
          <Typography variant="body2" sx={{
            color: 'text.secondary',
            textAlign: 'center',
            animation: 'fadeInOut 2s ease-in-out infinite',
            '@keyframes fadeInOut': {
              '0%, 100%': { opacity: 0.7 },
              '50%': { opacity: 1 },
            },
          }}>
            Инициализация Yandex Maps...
          </Typography>
        </Box>
      )}

      <YMaps query={{ apikey: (window as any)._env_?.REACT_APP_YANDEX_MAPS_API_KEY || process.env.REACT_APP_YANDEX_MAPS_API_KEY || '' }}>
        <Map
          defaultState={{
            center: mapCenter,
            zoom: 11,
            controls: []
          }}
          style={{
            width: '100%',
            height: '100%',
            filter: darkMode ? 'brightness(0.8) contrast(1.2) saturate(0.9)' : 'none',
            transition: 'filter 0.3s ease-in-out'
          }}
          modules={['control.ZoomControl', 'control.GeolocationControl', 'geoObject.addon.balloon', 'geoObject.addon.hint']}
          instanceRef={(map: any) => {
            mapRef.current = map;
          }}
          onClick={() => {
            try {
              mapRef.current?.balloon?.close?.();
            } catch (error) {
              console.warn('Не удалось закрыть балун по клику на карту:', error);
            }
          }}
          onLoad={() => {
            if (setMapLoading) {
              setMapLoading(false);
            }
          }}
        >
        <ZoomControl />
        <GeolocationControl />

        {trackBaseLine}

        {playedTrackCoordinates.length > 1 && (
          <Polyline
            geometry={playedTrackCoordinates}
            properties={{
              hintContent: `До ${formatTrackPointTime(currentTrackPoint?.dt)}`,
            }}
            options={{
              strokeColor: trackColor,
              strokeWidth: 5,
              strokeOpacity: 0.62,
              zIndex: 1201,
            }}
            modules={['geoObject.addon.hint']}
          />
        )}

        {trackCoordinates.length > 1 && (
          <>
            {trackStaticMarkers}
            {shouldShowTrackCursor && currentTrackPoint && (
              <StableTrackCursorPlacemark
                point={currentTrackPoint}
                iconHref={currentTrackMarkerIcon}
                hintContent={`Сейчас: ${formatTrackPointTime(currentTrackPoint.dt)}`}
                balloonHeader={`Точка трека: ${selectedTrack?.transport || selectedTrack?.vehicle?.transport || ''}`}
                balloonBody={getTrackPointBalloonBody(currentTrackPoint)}
              />
            )}
          </>
        )}

        {vehicleMarkers}
        {deliveryMarkers}
        {warehouseMarker}

        {false && (<>
        {vehiclePositions.map((vehicle, index) => {
          const speed = Number.isFinite(Number(vehicle.speed))
            ? Math.round(Number(vehicle.speed))
            : 0;
          const dt = vehicle.dt || 'не указано';
          const address = vehicle.address || 'Адрес не определен';
          const routeList = allRoutes || routes;
          const routeIndex = routeList.findIndex(r => r.routeNumber === vehicle.routeNumber);
          const routeColor = getRouteColorCSS(routeIndex >= 0 ? routeIndex : index);
          return (
            <Placemark
              key={`vehicle-${vehicle.unitId || index}`}
              geometry={[vehicle.lat, vehicle.lon]}
              properties={{
                balloonContentHeader: `${vehicle.stale ? '⚠ Устаревшая позиция' : '🚚'} ${vehicle.transport || vehicle.vehicleName || 'Машина'}`,
                balloonContentBody: `
                  <div style="font-family: Arial, sans-serif; line-height: 1.45; max-width: 320px;">
                    <div style="margin-bottom: 8px;">
                      <strong style="color: #0284c7;">Маршрут:</strong><br/>
                      <span>№${vehicle.routeNumber || 'не указан'}</span>
                    </div>
                    <div style="margin-bottom: 8px;">
                      <strong style="color: #0284c7;">Водитель:</strong><br/>
                      <span>${vehicle.driver || 'не указан'}</span>
                    </div>
                    <div style="margin-bottom: 8px; display: flex; gap: 16px; flex-wrap: wrap;">
                      <div>
                        <strong style="color: #0284c7;">Скорость:</strong><br/>
                        <span>${speed} км/ч</span>
                      </div>
                      <div>
                        <strong style="color: #0284c7;">Время:</strong><br/>
                        <span>${dt}</span>
                      </div>
                    </div>
                    <div>
                      <strong style="color: #0284c7;">Адрес:</strong><br/>
                      <span style="font-size: 12px; color: #666;">${address}</span>
                    </div>
                  </div>
                `,
              }}
              options={{
                iconLayout: 'default#image',
                iconImageHref: createVehicleMarker(routeColor, vehicle.stale ? 0.35 : 1),
                iconImageSize: [38, 52],
                iconImageOffset: [-19, -52],
                openBalloonOnClick: true,
                hideIconOnBalloonOpen: false,
                zIndex: 2000,
              }}
              modules={['geoObject.addon.balloon']}
            />
          );
        })}

        {deliveryPoints.map((delivery, index) => {
          // Проверяем, является ли эта точка активной
          const isActive = activeDeliveryPoint &&
            activeDeliveryPoint.routeNumber === delivery.routeNumber &&
            activeDeliveryPoint.invoiceNumber === delivery.invoiceNumber &&
            activeDeliveryPoint.deliveryId === delivery.id;

          // Предварительно вычисляем все значения
          const getActualTime = (report) => {
            if (!report) return 'Не указано';
            const match = report.match(/в (\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}:\d{2})/);
            return match ? match[1].split(' ')[1] : 'Не указано';
          };

          const getPlanTime = (estimatedTime) => {
            if (!estimatedTime || estimatedTime === '0001-01-01T09:29:18') return 'Не указано';
            try {
              return new Date(estimatedTime).toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'});
            } catch {
              return 'Не указано';
            }
          };

          // Получаем статус доставки на основе единой логики
          const deliveryStatus = getDeliveryStatusText(delivery);
          const statusColor = getDeliveryStatusColor(delivery);
          const statusText = `${deliveryStatus.emoji} ${deliveryStatus.text}`;

          const clientName = delivery.client || 'Не указан';
          const driverName = delivery.driver || 'Не указан';
          const planTime = getPlanTime(delivery.estimated_time);
          const actualTime = getActualTime(delivery.deliveryReport);
          const weight = delivery.weight || 0;
          const packages = delivery.packages || 'Не указано';
          const invoiceNumber = delivery.invoiceNumber || 'Не указана';
          const description = delivery.deliveryReport ? delivery.deliveryReport.replace(/\n/g, '<br/>').trim() : 'Нет описания';
          const mutedTextColor = darkMode ? '#cbd5e1' : '#475569';

          // Получаем цвета для кастомного маркера
          const routeColorHex = isActive ? '#212121' : getRouteColorCSS(delivery.routeIndex);
          const deliveryStatusColor = getDeliveryStatusColor(delivery);
          const customMarkerIcon = createMarkerWithBadge(
            delivery.id || '?',
            routeColorHex,
            deliveryStatusColor,
            delivery
          );

          return (
            <Placemark
              key={`${delivery.routeNumber}-${delivery.invoiceNumber}-${index}`}
              geometry={[
                parseFloat(delivery.location.latitude),
                parseFloat(delivery.location.longitude)
              ]}
              properties={{
                balloonContentHeader: `Точка доставки №${delivery.id}`,
                balloonContentBody: `
                  <div style="font-family: Arial, sans-serif; line-height: 1.4; max-width: 350px; color: ${darkMode ? '#e2e8f0' : '#1f2937'};">
                    <div style="margin-bottom: 8px;">
                      <strong style="color: #1976d2;">Клиент:</strong><br/>
                      <span>${clientName}</span>
                    </div>
                    <div style="margin-bottom: 8px;">
                      <strong style="color: #1976d2;">Водитель:</strong><br/>
                      <span>${driverName}</span>
                    </div>

                    <div style="margin-bottom: 8px; display: flex; gap: 16px; flex-wrap: wrap;">
                      <div>
                        <strong style="color: #1976d2;">Маршрут:</strong><br/>
                        <span>№${delivery.routeNumber}</span>
                      </div>
                      <div>
                        <strong style="color: #1976d2;">Накладная:</strong><br/>
                        <span>${invoiceNumber}</span>
                      </div>
                      <div>
                        <strong style="color: #1976d2;">Статус:</strong><br/>
                        <span style="color: ${statusColor};">${statusText}</span>
                      </div>
                    </div>

                    <div style="margin-bottom: 8px; display: flex; gap: 16px; flex-wrap: wrap;">
                      <div>
                        <strong style="color: #1976d2;">Время план/факт:</strong><br/>
                        <span>План: ${planTime}</span><br/>
                        <span>Факт: ${actualTime}</span>
                      </div>
                      <div>
                        <strong style="color: #1976d2;">Вес/Упаковки:</strong><br/>
                        <span>⚖️ ${weight} кг</span><br/>
                        <span>📦 ${packages}</span>
                      </div>
                    </div>

                    <div style="margin-bottom: 8px;">
                      <strong style="color: #1976d2;">Описание:</strong><br/>
                      <span style="font-size: 12px; color: ${mutedTextColor};">
                        ${description}
                      </span>
                    </div>
                  </div>
                `,
                balloonContentFooter: ``
              }}
              options={{
                iconLayout: 'default#image',
                iconImageHref: customMarkerIcon,
                iconImageSize: [26, 32],
                iconImageOffset: [-13, -32],
                openBalloonOnClick: true,
                hideIconOnBalloonOpen: false,
                zIndex: isActive ? 1000 : 100
              }}
              modules={['geoObject.addon.balloon']}
            />
          );
        })}

        {/* Маркер склада */}
        <Placemark
          geometry={[42.883564, 47.618345]}
          properties={{
            balloonContentHeader: '🏢 Склад',
            balloonContentBody: `
              <div style="font-family: Arial, sans-serif; line-height: 1.4;">
                <strong>Главный склад</strong><br/>
                <span style="color: #666; font-size: 12px;">Точка отправки всех маршрутов</span>
              </div>
            `
          }}
          options={{
            iconLayout: 'default#image',
            iconImageHref: createWarehouseMarker(),
            iconImageSize: [30, 36],
            iconImageOffset: [-15, -36],
            openBalloonOnClick: true,
            hideIconOnBalloonOpen: false
          }}
          modules={['geoObject.addon.balloon']}
        />
        </>)}
        </Map>
      </YMaps>

      {trackPoints.length > 1 && (
        <Box sx={{
          position: 'absolute',
          left: { xs: 8, sm: 14 },
          right: { xs: 8, sm: 14 },
          bottom: { xs: 8, sm: 14 },
          zIndex: 1100,
          display: 'grid',
          gridTemplateColumns: { xs: '40px 1fr', sm: '44px 1fr auto' },
          alignItems: 'center',
          gap: { xs: 1, sm: 1.5 },
          px: { xs: 1, sm: 1.5 },
          py: { xs: 0.75, sm: 1 },
          borderRadius: 2,
          backgroundColor: darkMode ? 'rgba(15, 23, 42, 0.92)' : 'rgba(255, 255, 255, 0.94)',
          border: '1px solid',
          borderColor: darkMode ? 'rgba(148, 163, 184, 0.28)' : 'rgba(15, 23, 42, 0.14)',
          boxShadow: darkMode ? '0 14px 34px rgba(0,0,0,0.34)' : '0 14px 34px rgba(15,23,42,0.18)',
          backdropFilter: 'blur(10px)',
        }}>
          <IconButton
            size="small"
            onClick={() => setTrackPlaying((prev) => !prev)}
            sx={{
              width: 38,
              height: 38,
              color: 'white',
              backgroundColor: selectedTrack?.color || '#0284c7',
              '&:hover': {
                backgroundColor: selectedTrack?.color || '#0369a1',
                filter: 'brightness(0.92)',
              },
            }}
            aria-label={trackPlaying ? 'Пауза' : 'Запуск трека'}
          >
            {trackPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
          </IconButton>

          <Box sx={{ minWidth: 0 }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 0.25,
              color: darkMode ? '#e2e8f0' : '#0f172a',
            }}>
              <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                {formatTrackPointTime(currentTrackPoint?.dt)}
              </Typography>
              <Typography variant="caption" sx={{
                color: darkMode ? '#cbd5e1' : '#475569',
                whiteSpace: 'nowrap',
              }}>
                {currentTrackSpeed}
              </Typography>
            </Box>
            <Slider
              size="small"
              min={0}
              max={trackPoints.length - 1}
              step={1}
              value={currentTrackIndex}
              onChange={(_, value) => {
                setTrackPlaying(false);
                setTrackCursorIndex(Array.isArray(value) ? value[0] : value);
              }}
              sx={{
                display: 'block',
                py: 0.5,
                color: selectedTrack?.color || '#0284c7',
                '& .MuiSlider-thumb': {
                  width: 16,
                  height: 16,
                  boxShadow: '0 0 0 4px rgba(255,255,255,0.72)',
                },
                '& .MuiSlider-rail': {
                  opacity: darkMode ? 0.36 : 0.22,
                },
              }}
            />
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1,
              color: darkMode ? '#94a3b8' : '#64748b',
            }}>
              <Typography variant="caption" sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                {formatTrackPointTime(trackPoints[0]?.dt)}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                {formatTrackPointTime(trackPoints[trackPoints.length - 1]?.dt)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{
            display: { xs: 'none', sm: 'block' },
            minWidth: 132,
            textAlign: 'right',
            color: darkMode ? '#cbd5e1' : '#334155',
          }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
              Точка {currentTrackIndex + 1} / {trackPoints.length}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: darkMode ? '#94a3b8' : '#64748b' }}>
              тяните ползунок
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Компонент для отображения маршрута (реальные данные 1С)
const RouteCard: React.FC<{
  route: any;
  clientFilter?: string;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
  darkMode?: boolean;
}> = ({ route, clientFilter = '', isExpanded = false, onToggleExpanded, darkMode = false }) => {
  const [isDeliveryTableExpanded, setIsDeliveryTableExpanded] = React.useState(true);

  // Вычисляем общее количество мест для маршрута
  const totalPackages = (route.deliveries || []).reduce((sum: number, d: any) => {
    if (d.packages) {
      const numbers = d.packages.match(/\d+/g) || [];
      const packagesSum = numbers.reduce((s: number, n: string) => s + parseInt(n, 10), 0);
      return sum + packagesSum;
    }
    return sum;
  }, 0);

  const displayStatus = mapRouteStatus(route.routeStatus);

  // Получаем цвета для статуса
  const getStatusColors = (status: string) => {
    if (status === 'Завершено') return { gradient: '#10b981, #059669', bg: 'rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.1)', chip: 'success' as const };
    if (status === 'В пути') return { gradient: '#f59e0b, #d97706', bg: 'rgba(245, 158, 11, 0.1), rgba(217, 119, 6, 0.1)', chip: 'warning' as const };
    if (status === 'Ожидание') return { gradient: '#06b6d4, #0891b2', bg: 'rgba(6, 182, 212, 0.1), rgba(8, 145, 178, 0.1)', chip: 'info' as const };
    return { gradient: '#4f46e5, #7c3aed', bg: 'rgba(79, 70, 229, 0.1), rgba(124, 58, 237, 0.1)', chip: 'default' as const };
  };

  const statusColors = getStatusColors(displayStatus);

  return (
  <Box sx={{
    border: '1px solid',
    borderColor: 'rgba(226, 232, 240, 0.8)',
    borderRadius: 4,
    p: 0,
    mb: 3,
    backgroundColor: 'background.paper',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    position: 'relative',
    '&:hover': {
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
      transform: 'translateY(-4px)',
      borderColor: 'primary.main',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: `linear-gradient(135deg, ${statusColors.gradient})`,
    }
  }}>
    {/* Header с градиентным фоном */}
    <Box sx={{
      background: `linear-gradient(135deg, ${statusColors.bg})`,
      p: 3,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: { xs: 'flex-start', sm: 'center' },
      flexDirection: { xs: 'column', sm: 'row' },
      gap: { xs: 2, sm: 0 },
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        cursor: 'pointer',
        flex: 1,
        width: { xs: '100%', sm: 'auto' },
        transition: 'all 0.2s ease-in-out',
        borderRadius: 2,
        p: 1,
        ml: -1,
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
        }
      }}
      onClick={onToggleExpanded || (() => {})}
      >
        <Box sx={{
          width: 48,
          height: 48,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${statusColors.gradient})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        }}>
          <LocalShippingIcon sx={{ fontSize: 24, color: 'white' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="h5" sx={{
              color: 'primary.main',
              fontWeight: 700,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              background: `linear-gradient(135deg, ${statusColors.gradient})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              Маршрут №{route.routeNumber}
            </Typography>
            {route.hasOutOfRouteDeliveries && (
              <Chip
                label="Вне маршрута"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            )}
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {route.driver}
          </Typography>
        </Box>
        <ExpandMoreIcon sx={{
          color: 'text.secondary',
          transition: 'transform 0.3s ease-in-out',
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          fontSize: 28,
        }} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, alignSelf: { xs: 'flex-start', sm: 'center' } }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DirectionsIcon />}
          onClick={(e) => {
            e.stopPropagation();
            const routeURL = createYandexRouteURL(route);
            if (routeURL) {
              window.open(routeURL, '_blank');
            }
          }}
          sx={{
            borderColor: 'primary.main',
            color: 'primary.main',
            '&:hover': {
              borderColor: 'primary.dark',
              backgroundColor: 'primary.main',
              color: 'white',
            },
            fontSize: '0.75rem',
            height: 32,
            px: 1.5,
          }}
        >
          Маршрут
        </Button>
        <Chip
          label={displayStatus}
          color={statusColors.chip}
          size="medium"
          sx={{
            fontWeight: 600,
            fontSize: '0.875rem',
            height: 36,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        />
      </Box>
    </Box>

    {isExpanded && (
      <Box sx={{ p: 3, pt: 0 }}>
        {/* Информационные карточки */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3
        }}>
          <Box sx={{
            p: 2.5,
            backgroundColor: 'grey.50',
            borderRadius: 3,
            border: '1px solid rgba(226, 232, 240, 0.6)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'grey.100',
              transform: 'translateY(-2px)',
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <CalendarTodayIcon sx={{ fontSize: 20, color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Дата
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {new Date(route.routeDate + 'T00:00:00').toLocaleDateString('ru-RU')}
            </Typography>
          </Box>

          <Box sx={{
            p: 2.5,
            backgroundColor: 'grey.50',
            borderRadius: 3,
            border: '1px solid rgba(226, 232, 240, 0.6)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'grey.100',
              transform: 'translateY(-2px)',
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <DirectionsCarIcon sx={{ fontSize: 20, color: 'warning.main' }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Транспорт
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.9rem' }}>
              {route.transport}
            </Typography>
          </Box>

          <Box sx={{
            p: 2.5,
            backgroundColor: 'grey.50',
            borderRadius: 3,
            border: '1px solid rgba(226, 232, 240, 0.6)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'grey.100',
              transform: 'translateY(-2px)',
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <LocationOnIcon sx={{ fontSize: 20, color: 'info.main' }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Расстояние
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {route.distance || 0} км
            </Typography>
          </Box>

          <Box sx={{
            p: 2.5,
            backgroundColor: 'grey.50',
            borderRadius: 3,
            border: '1px solid rgba(226, 232, 240, 0.6)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'grey.100',
              transform: 'translateY(-2px)',
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <ScaleIcon sx={{ fontSize: 20, color: 'success.main' }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Вес / Мест
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {(route.weight_kg || 0).toFixed(3)} кг / {totalPackages} шт
            </Typography>
          </Box>
        </Box>

        {/* Контактная информация */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 2,
          mb: 3
        }}>
          <Box sx={{
            p: { xs: 2, sm: 3 },
            background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.1), rgba(124, 58, 237, 0.1))',
            borderRadius: 3,
            border: '1px solid rgba(79, 70, 229, 0.2)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <PersonIcon sx={{ fontSize: 24, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Водитель
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              {route.driver}
            </Typography>
            <Box component="a" href={`tel:${route.driverPhone}`} sx={{
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'primary.main',
              '&:hover': { color: 'primary.dark' }
            }}>
              <PhoneIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {route.driverPhone}
              </Typography>
            </Box>
          </Box>

          {route.loading_start && (
            <Box sx={{
              p: { xs: 2, sm: 3 },
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.1))',
              borderRadius: 3,
              border: '1px solid rgba(16, 185, 129, 0.2)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <AccessTimeIcon sx={{ fontSize: 24, color: 'success.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                  Погрузка
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {route.loading_start.replace('T', ' ').substring(11, 16)} - {route.loading_stop.replace('T', ' ').substring(11, 16)}
              </Typography>
              {route.loading_check && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Проверил: {route.loading_check}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    )}

    {isExpanded && (
      <>
    {/* Показываем дополнительную информацию из файла 1С если есть */}

    {/* Таблица точек доставки */}
    {route.deliveries && route.deliveries.length > 0 && (
      <Box sx={{ mt: 0 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          position: 'relative'
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            borderRadius: 2,
            p: 1.5,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.08) 0%, rgba(6, 182, 212, 0.08) 100%)',
              transform: 'scale(1.02)',
              boxShadow: '0 4px 12px rgba(79, 70, 229, 0.15)',
            }
          }}
          onClick={() => setIsDeliveryTableExpanded(!isDeliveryTableExpanded)}
          >
            <ExpandMoreIcon sx={{
              color: 'text.secondary',
              transition: 'transform 0.2s ease-in-out',
              transform: isDeliveryTableExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              cursor: 'pointer'
            }} />
            <Typography variant="h6" sx={{
              fontWeight: 600,
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <InventoryIcon sx={{ fontSize: 'inherit' }} />
              Точки доставки
              <Chip label={route.deliveries.length} size="small" color="primary" variant="outlined" />
            </Typography>
          </Box>
        </Box>
        {isDeliveryTableExpanded && (
          <>
            {/* Десктоп версия - обычная таблица */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                  '& .MuiTableHead-root': {
                    '& .MuiTableCell-root': {
                      backgroundColor: 'grey.50',
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }
                  }
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>№/Накладная</TableCell>
                      <TableCell>Клиент</TableCell>
                      <TableCell>Вес/Упаковки</TableCell>
                      <TableCell>Время план/факт</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Описание</TableCell>
                      <TableCell>Менеджер</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {route.deliveries.map((delivery: any, index: number) => {
                      // Проверяем, найден ли клиент в этой строке
                      const isHighlighted = clientFilter.trim() &&
                        delivery.client?.toLowerCase().includes(clientFilter.toLowerCase());

                      return (
                      <TableRow
                        key={delivery.id || index}
                        sx={{
                          backgroundColor: isHighlighted ? '#fff3cd' : 'transparent',
                          '&:hover': {
                            backgroundColor: isHighlighted ? '#fff3cd' : 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        <TableCell sx={{ minWidth: 80 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            №{delivery.id}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                            {delivery.invoiceNumber}
                          </Typography>
                          {delivery.outOfRoute && (
                            <Chip
                              label="Вне маршрута"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ mt: 0.5, height: 18, fontSize: '0.62rem' }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {delivery.location && delivery.location.latitude && delivery.location.longitude && (
                              <Box
                                component="a"
                                href={`https://yandex.ru/maps/?pt=${delivery.location.longitude},${delivery.location.latitude}&z=16&l=map`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  color: 'primary.main',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    color: 'primary.dark',
                                  }
                                }}
                              >
                                <PlaceIcon sx={{ fontSize: 16 }} />
                              </Box>
                            )}
                            <Typography sx={{
                              flex: 1,
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              lineHeight: 1.3
                            }}>
                              {delivery.client}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {delivery.weight} кг
                          {delivery.packages && (
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                              {delivery.packages}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 100 }}>
                          {(() => {
                            // Плановое время (из estimated_time)
                            const estimatedTime = delivery.estimated_time ?
                              new Date(delivery.estimated_time).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) :
                              '';

                            // Фактическое время (парсим из deliveryReport)
                            const report = delivery.deliveryReport?.trim() || '';
                            let actualTime = '';

                            // Ищем дату в формате "в 02.09.2025 10:36:33" или "в 03.09.2025 11:13:54"
                            const dateMatch = report.match(/в (\d{2}\.\d{2}\.\d{4}) (\d{2}:\d{2})/);
                            if (dateMatch) {
                              actualTime = dateMatch[2]; // Берем только время HH:MM
                            }

                            return (
                              <Box>
                                {estimatedTime && (
                                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
                                    <AccessTimeIcon sx={{ fontSize: 12 }} />
                                    {estimatedTime}
                                  </Typography>
                                )}
                                {actualTime && (
                                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main', fontWeight: 500 }}>
                                    <CheckCircleIcon sx={{ fontSize: 12 }} />
                                    {actualTime}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const report = delivery.deliveryReport?.trim() || '';

                            // 1) Еще не доставляли и не пытались (пустой отчет)
                            if (!report) {
                              return <HourglassEmptyIcon sx={{ fontSize: 20, color: 'text.secondary' }} />;
                            }

                            // 2) Попытались и доставили (есть слово "Доставлено")
                            if (report.includes('Доставлено')) {
                              return <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />;
                            }

                            // 3) Попытались, но не приняли (есть "Попытка доставки")
                            if (report.includes('Попытка доставки')) {
                              return <CancelIcon sx={{ fontSize: 20, color: 'error.main' }} />;
                            }

                            // Резервный случай - если есть отчет, но не подходит под шаблоны
                            return <HourglassEmptyIcon sx={{ fontSize: 20, color: 'warning.main' }} />;
                          })()}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          {delivery.deliveryReport && (
                            <Typography variant="caption" sx={{
                              display: 'block',
                              color: 'text.secondary',
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              lineHeight: 1.2
                            }}>
                              {delivery.deliveryReport.trim()}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 140, maxWidth: 160 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {delivery.manager}
                          </Typography>
                          {delivery.managerPhone && (
                            <Box component="a" href={`tel:${delivery.managerPhone}`} sx={{ textDecoration: 'none', display: 'block' }}>
                              <Typography variant="caption" sx={{ display: 'block', color: 'primary.main' }}>
                                {delivery.managerPhone}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Мобильная версия - двухуровневые карточки */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              {route.deliveries.map((delivery: any, index: number) => {
                // Проверяем, найден ли клиент в этой строке
                const isHighlighted = clientFilter.trim() &&
                  delivery.client?.toLowerCase().includes(clientFilter.toLowerCase());

                // Вычисляем данные для отображения
                const estimatedTime = delivery.estimated_time ?
                  new Date(delivery.estimated_time).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) :
                  '';

                const report = delivery.deliveryReport?.trim() || '';
                let actualTime = '';
                const dateMatch = report.match(/в (\d{2}\.\d{2}\.\d{4}) (\d{2}:\d{2})/);
                if (dateMatch) {
                  actualTime = dateMatch[2];
                }

                // Определяем статус
                let StatusIcon;
                let statusColor;
                if (!report) {
                  StatusIcon = HourglassEmptyIcon;
                  statusColor = 'text.secondary';
                } else if (report.includes('Доставлено')) {
                  StatusIcon = CheckCircleIcon;
                  statusColor = 'success.main';
                } else if (report.includes('Попытка доставки')) {
                  StatusIcon = CancelIcon;
                  statusColor = 'error.main';
                } else {
                  StatusIcon = HourglassEmptyIcon;
                  statusColor = 'warning.main';
                }

                return (
                  <Paper
                    key={delivery.id || index}
                    sx={{
                      p: 2,
                      mb: 1,
                      borderRadius: 2,
                      backgroundColor: isHighlighted ? (darkMode ? 'rgba(245, 158, 11, 0.2)' : '#fff3cd') : 'background.paper',
                      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
                      border: isHighlighted ? '1px solid #ffc107' : '1px solid #e0e0e0'
                    }}
                  >
                    {/* 1-й уровень: ID, Накладная, Время, Вес */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 2,
                      mb: 1
                    }}>
                      <Typography variant="body2" sx={{
                        fontWeight: 600,
                        color: 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}>
                        <InventoryIcon sx={{ fontSize: 16 }} />
                        №{delivery.id}
                      </Typography>
                      <Typography variant="body2" sx={{
                        color: 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}>
                        <DescriptionIcon sx={{ fontSize: 16 }} />
                        {delivery.invoiceNumber}
                      </Typography>
                      {delivery.outOfRoute && (
                        <Chip
                          label="Вне маршрута"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      )}
                      {(estimatedTime || actualTime) && (
                        <Typography variant="body2" sx={{
                          color: 'text.secondary',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}>
                          <AccessTimeIcon sx={{ fontSize: 16 }} />
                          {actualTime || estimatedTime}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{
                        color: 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}>
                        <ScaleIcon sx={{ fontSize: 16 }} />
                        {delivery.weight}кг {delivery.packages && `/ ${delivery.packages}`}
                      </Typography>
                    </Box>

                    {/* 2-й уровень: Клиент + Статус */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1,
                      mb: 1
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1 }}>
                        {delivery.location && delivery.location.latitude && delivery.location.longitude && (
                          <Box
                            component="a"
                            href={`https://yandex.ru/maps/?pt=${delivery.location.longitude},${delivery.location.latitude}&z=16&l=map`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              color: 'primary.main',
                              textDecoration: 'none',
                              mt: 0.2,
                              '&:hover': {
                                color: 'primary.dark',
                              }
                            }}
                          >
                            <PlaceIcon sx={{ fontSize: 16 }} />
                          </Box>
                        )}
                        <Typography variant="body2" sx={{
                          flex: 1,
                          color: 'text.primary',
                          lineHeight: 1.3,
                          wordBreak: 'break-word',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 0.5
                        }}>
                          <PersonIcon sx={{ fontSize: 16, mt: 0.1 }} />
                          {delivery.client}
                        </Typography>
                      </Box>
                      <StatusIcon sx={{ color: statusColor, fontSize: 20 }} />
                    </Box>

                    {/* 3-й уровень: Описание */}
                    {delivery.deliveryReport && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 0.5,
                          color: 'text.secondary',
                          lineHeight: 1.2,
                          wordBreak: 'break-word'
                        }}>
                          <NotesIcon sx={{ fontSize: 14, mt: 0.1 }} />
                          {delivery.deliveryReport.trim()}
                        </Typography>
                      </Box>
                    )}

                    {/* 4-й уровень: Менеджер + Телефон */}
                    {delivery.manager && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{
                          color: 'text.secondary',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}>
                          <BadgeIcon sx={{ fontSize: 14 }} />
                          {delivery.manager}
                        </Typography>
                        {delivery.managerPhone && (
                          <Box
                            component="a"
                            href={`tel:${delivery.managerPhone}`}
                            sx={{ textDecoration: 'none' }}
                          >
                            <Typography variant="caption" sx={{
                              color: 'primary.main',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}>
                              <PhoneIcon sx={{ fontSize: 14 }} />
                              {delivery.managerPhone}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Box>
          </>
        )}
      </Box>
    )}
      </>
    )}
  </Box>
  );
};

// Главная страница мониторинга
const HomePage: React.FC = () => {
  const [darkMode, setDarkMode] = React.useState<boolean>(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [status, setStatus] = React.useState<'loading' | 'success' | 'error'>('loading');
  const [routes, setRoutes] = React.useState<any[]>([]);
  const [lastUpdate, setLastUpdate] = React.useState<string>('');
  const [selectedDate, setSelectedDate] = React.useState<string>(moscowDate());
  const [loading, setLoading] = React.useState<boolean>(false);
  const [mapLoading, setMapLoading] = React.useState<boolean>(true);
  const [availableDates, setAvailableDates] = React.useState<string[]>([]);
  const [vehiclePositions, setVehiclePositions] = React.useState<any[]>([]);
  const [vehiclePositionsMeta, setVehiclePositionsMeta] = React.useState<any>(null);
  const [selectedTrack, setSelectedTrack] = React.useState<any | null>(null);
  const [trackLoadingKey, setTrackLoadingKey] = React.useState<string | null>(null);
  const [listClientFilter, setListClientFilter] = React.useState<string>('');
  const [mapClientFilter, setMapClientFilter] = React.useState<string>('');
  const [hybridClientFilter, setHybridClientFilter] = React.useState<string>('');
  const [currentPage, setCurrentPage] = React.useState<number>(0); // 0: Список, 1: Карта, 2: Гибрид
  const [expandedRoutes, setExpandedRoutes] = React.useState<Record<string, boolean>>({});
  const [expandedHybridRoutes, setExpandedHybridRoutes] = React.useState<Record<string, boolean>>({});
  const [checkedHybridRoutes, setCheckedHybridRoutes] = React.useState<Record<string, boolean>>({});
  const [activeDeliveryPoint, setActiveDeliveryPoint] = React.useState<{
    routeNumber: string;
    invoiceNumber: string;
    deliveryId: string;
    coordinates?: [number, number];
  } | null>(null);

  // Состояние для кастомного календаря
  const [calendarOpen, setCalendarOpen] = React.useState(false);
  const [calendarAnchor, setCalendarAnchor] = React.useState<HTMLElement | null>(null);

  // Функция переключения темы
  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', JSON.stringify(newMode));
  };

  // Создаем тему на основе текущего режима
  const theme = React.useMemo(() => createAppTheme(darkMode ? 'dark' : 'light'), [darkMode]);

  const isToday = React.useCallback((date: string) => {
    return date === moscowDate();
  }, []);

  // Функции для управления всеми маршрутами
  const expandAllRoutes = () => {
    const expanded: Record<string, boolean> = {};
    routes.forEach(route => {
      expanded[route.routeNumber] = true;
    });
    setExpandedRoutes(expanded);
  };

  const collapseAllRoutes = () => {
    const collapsed: Record<string, boolean> = {};
    routes.forEach(route => {
      collapsed[route.routeNumber] = false;
    });
    setExpandedRoutes(collapsed);
  };

  // Функции для управления чекбоксами в гибридном режиме
  const checkAllHybridRoutes = () => {
    const checked: Record<string, boolean> = {};
    hybridFilteredRoutes.forEach(route => {
      checked[route.routeNumber] = true;
    });
    setCheckedHybridRoutes(prev => ({ ...prev, ...checked }));
  };

  const uncheckAllHybridRoutes = () => {
    const unchecked: Record<string, boolean> = {};
    hybridFilteredRoutes.forEach(route => {
      unchecked[route.routeNumber] = false;
    });
    setCheckedHybridRoutes(prev => ({ ...prev, ...unchecked }));
  };



  // Функция загрузки РЕАЛЬНЫХ данных из файла 1С
  const activeRouteRequest = React.useRef(0);
  const activeDate = React.useRef(selectedDate);
  activeDate.current = selectedDate;
  const loadVehiclePositions = React.useCallback(async (date: string) => {
    if (!isToday(date)) {
      setVehiclePositions([]);
      setVehiclePositionsMeta({ enabled: false, reason: 'not_today' });
      return;
    }

    try {
      // @ts-ignore
      const apiUrl = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/v1/vehicles/positions?date=${encodeURIComponent(date)}`);
      if (!response.ok) {
        throw new Error('Ошибка получения позиций машин');
      }
      const data = await response.json();
      if (activeDate.current !== date) return;
      setVehiclePositions(data.data || []);
      setVehiclePositionsMeta(data.meta || null);
    } catch (error) {
      if (activeDate.current !== date) return;
      console.error('Ошибка загрузки позиций машин:', error);
      setVehiclePositions([]);
      setVehiclePositionsMeta({ enabled: true, error: true });
    }
  }, [isToday]);

  const loadRoutesForDate = React.useCallback(async (date: string, quiet = false) => {
    const requestId = ++activeRouteRequest.current;
    if (!quiet) {
      setLoading(true);
      setRoutes([]);
      setVehiclePositions([]);
      setVehiclePositionsMeta(null);
      setSelectedTrack(null);
    }
    try {
      // Читаем данные напрямую из файла 1С
      // @ts-ignore
      const apiUrl = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/v1/data/raw?date=${encodeURIComponent(date)}`);

      if (response.ok) {
        const data = await response.json();
        if (requestId !== activeRouteRequest.current || activeDate.current !== date) return;
        const allRoutes = data.routes || [];

        // Фильтруем маршруты по выбранной дате
        const routesForDate = allRoutes.filter((route: any) => {
          return route.routeDate === date;
        });

        // Сортируем маршруты по номеру для стабильного порядка цветов
        const sortedRoutes = routesForDate.sort((a: any, b: any) => {
          return a.routeNumber.localeCompare(b.routeNumber);
        });

        // Получаем все уникальные даты и сортируем
        const uniqueDates: string[] = data.availableDates || [...new Set<string>(allRoutes.map((r: any) => r.routeDate))].sort().reverse();
        setAvailableDates(uniqueDates);

        setRoutes(sortedRoutes);
        if (!quiet) setSelectedTrack(null);

        // Инициализируем чекбоксы - по умолчанию все маршруты включены
        const initialCheckedState: Record<string, boolean> = {};
        routesForDate.forEach((route: any) => {
          initialCheckedState[route.routeNumber] = true;
        });
        setCheckedHybridRoutes(prev => quiet ? { ...initialCheckedState, ...prev } : initialCheckedState);

        setStatus('success');
        setLastUpdate(new Date().toLocaleString());
        await loadVehiclePositions(date);

      } else {
        throw new Error('Ошибка получения данных');
      }
    } catch (error) {
      if (requestId !== activeRouteRequest.current || activeDate.current !== date) return;
      console.error('Ошибка загрузки данных:', error);
      setStatus('error');
      setRoutes([]);
      setVehiclePositions([]);
      setVehiclePositionsMeta(null);
    } finally {
      if (requestId === activeRouteRequest.current) setLoading(false);
    }
  }, [loadVehiclePositions]);

  const loadRouteTrack = async (route: any, color: string) => {
    const trackDate = selectedDate;
    const key = `${route.routeNumber}-${selectedDate}`;
    setTrackLoadingKey(key);
    try {
      // @ts-ignore
      const apiUrl = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/v1/vehicles/track?transport=${encodeURIComponent(route.transport || '')}&date=${encodeURIComponent(selectedDate)}`);
      if (!response.ok) {
        throw new Error('Ошибка получения трека');
      }
      const data = await response.json();
      if (activeDate.current !== trackDate) return;
      setSelectedTrack({
        ...(data.data || {}),
        routeNumber: route.routeNumber,
        transport: route.transport,
        color,
      });
      setCheckedHybridRoutes(prev => ({
        ...prev,
        [route.routeNumber]: true
      }));
    } catch (error) {
      if (activeDate.current !== trackDate) return;
      console.error('Ошибка загрузки трека:', error);
      setSelectedTrack({
        routeNumber: route.routeNumber,
        transport: route.transport,
        color,
        points: [],
        reason: 'track_load_error',
      });
    } finally {
      setTrackLoadingKey(null);
    }
  };

  React.useEffect(() => {
    if (!isToday(selectedDate)) return;
    const id = window.setInterval(() => {
      loadVehiclePositions(selectedDate);
    }, 60000);
    return () => window.clearInterval(id);
  }, [selectedDate, isToday, loadVehiclePositions]);

  React.useEffect(() => {
    const id = window.setInterval(() => loadRoutesForDate(selectedDate, true), 60000);
    return () => window.clearInterval(id);
  }, [selectedDate, loadRoutesForDate]);

  // Фильтрация маршрутов для каждой вкладки
  const listFilteredRoutes = React.useMemo(() => {
    if (!listClientFilter.trim()) {
      return routes;
    }
    return routes.filter(route => {
      return route.deliveries?.some((delivery: any) =>
        delivery.client?.toLowerCase().includes(listClientFilter.toLowerCase())
      );
    });
  }, [routes, listClientFilter]);

  const mapFilteredRoutes = React.useMemo(() => {
    if (!mapClientFilter.trim()) {
      return routes;
    }
    return routes.filter(route => {
      return route.deliveries?.some((delivery: any) =>
        delivery.client?.toLowerCase().includes(mapClientFilter.toLowerCase())
      );
    });
  }, [routes, mapClientFilter]);

  const hybridFilteredRoutes = React.useMemo(() => {
    if (!hybridClientFilter.trim()) {
      return routes;
    }
    return routes.filter(route => {
      return route.deliveries?.some((delivery: any) =>
        delivery.client?.toLowerCase().includes(hybridClientFilter.toLowerCase())
      );
    });
  }, [routes, hybridClientFilter]);

  // Дополнительная фильтрация для карты в гибридном режиме с учетом чекбоксов
  const hybridMapRoutes = React.useMemo(() => {
    return hybridFilteredRoutes.filter(route =>
      checkedHybridRoutes[route.routeNumber] === true
    );
  }, [hybridFilteredRoutes, checkedHybridRoutes]);

  // Список всех точек доставки для навигации стрелками (только для гибридного режима)
  const allDeliveryPoints = React.useMemo(() => {
    if (currentPage !== 2) return []; // Только для гибридного режима

    return hybridFilteredRoutes.flatMap(route =>
      (route.deliveries || [])
        .filter((delivery: any) => delivery.location?.latitude && delivery.location?.longitude)
        .map((delivery: any) => ({
          routeNumber: route.routeNumber,
          invoiceNumber: delivery.invoiceNumber,
          deliveryId: delivery.id,
          client: delivery.client,
          coordinates: [parseFloat(delivery.location.latitude), parseFloat(delivery.location.longitude)] as [number, number]
        }))
    );
  }, [hybridFilteredRoutes, currentPage]);

  // Обработчик клавиш для навигации стрелками
  React.useEffect(() => {
    const navigateToPoint = (direction: 'up' | 'down') => {
      if (allDeliveryPoints.length === 0 || !activeDeliveryPoint) return;

      // Найти текущий маршрут
      const currentRoute = hybridFilteredRoutes.find(route =>
        route.routeNumber === activeDeliveryPoint.routeNumber
      );

      if (!currentRoute) return;

      // Получить точки только текущего маршрута
      const currentRoutePoints = (currentRoute.deliveries || [])
        .filter((delivery: any) => delivery.location?.latitude && delivery.location?.longitude)
        .map((delivery: any) => ({
          routeNumber: currentRoute.routeNumber,
          invoiceNumber: delivery.invoiceNumber,
          deliveryId: delivery.id,
          client: delivery.client,
          coordinates: [parseFloat(delivery.location.latitude), parseFloat(delivery.location.longitude)] as [number, number]
        }));

      if (currentRoutePoints.length === 0) return;

      // Найти текущий индекс в рамках маршрута
      const currentIndex = currentRoutePoints.findIndex(point =>
        point.invoiceNumber === activeDeliveryPoint.invoiceNumber &&
        point.deliveryId === activeDeliveryPoint.deliveryId
      );

      if (currentIndex === -1) return;

      let nextIndex;
      if (direction === 'down') {
        // Если не последняя точка в маршруте, переходим к следующей
        if (currentIndex < currentRoutePoints.length - 1) {
          nextIndex = currentIndex + 1;
        } else {
          // Если последняя точка - остаемся на ней (не переходим к другому маршруту)
          return;
        }
      } else {
        // Если не первая точка в маршруте, переходим к предыдущей
        if (currentIndex > 0) {
          nextIndex = currentIndex - 1;
        } else {
          // Если первая точка - остаемся на ней (не переходим к другому маршруту)
          return;
        }
      }

      const nextPoint = currentRoutePoints[nextIndex];
      setActiveDeliveryPoint(nextPoint);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // Работает только на вкладке "Гибрид" и когда есть точки доставки
      if (currentPage === 2 && allDeliveryPoints.length > 0) {
        if (event.key === 'ArrowDown') {
          event.preventDefault(); // Отменяем обычное поведение скролла
          navigateToPoint('down');
        } else if (event.key === 'ArrowUp') {
          event.preventDefault(); // Отменяем обычное поведение скролла
          navigateToPoint('up');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentPage, allDeliveryPoints, activeDeliveryPoint, hybridFilteredRoutes]);

  React.useEffect(() => {
    loadRoutesForDate(selectedDate);
  }, [selectedDate, loadRoutesForDate]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          '[class*="balloon__layout"]': {
            background: darkMode
              ? 'rgba(15, 23, 42, 0.78) !important'
              : 'rgba(255, 255, 255, 0.78) !important',
            backdropFilter: 'blur(14px) saturate(1.2)',
            WebkitBackdropFilter: 'blur(14px) saturate(1.2)',
            border: darkMode
              ? '1px solid rgba(148, 163, 184, 0.35)'
              : '1px solid rgba(255, 255, 255, 0.82)',
            borderRadius: '14px !important',
            boxShadow: '0 18px 45px rgba(15, 23, 42, 0.25) !important',
            overflow: 'hidden',
          },
          '[class*="balloon__content"]': {
            background: 'transparent !important',
            color: darkMode ? '#f8fafc !important' : '#0f172a !important',
          },
          '[class*="balloon__tail"]': {
            background: 'transparent !important',
          },
          '[class*="balloon__tail"]::after': {
            background: darkMode
              ? 'rgba(15, 23, 42, 0.78) !important'
              : 'rgba(255, 255, 255, 0.78) !important',
            backdropFilter: 'blur(14px) saturate(1.2)',
            WebkitBackdropFilter: 'blur(14px) saturate(1.2)',
          },
          '[class*="balloon__close-button"]': {
            opacity: '1 !important',
            filter: 'none !important',
            backgroundImage: 'none !important',
            background: darkMode
              ? 'rgba(248, 250, 252, 0.14) !important'
              : 'rgba(15, 23, 42, 0.08) !important',
            border: darkMode
              ? '1px solid rgba(248, 250, 252, 0.26) !important'
              : '1px solid rgba(15, 23, 42, 0.12) !important',
            borderRadius: '999px !important',
            position: 'relative !important',
            width: '32px !important',
            height: '32px !important',
            margin: '8px 8px 0 0 !important',
          },
          '[class*="balloon__close"]': {
            opacity: '1 !important',
          },
          '[class*="balloon__close-button"], [class*="balloon__close-button"]:before, [class*="balloon__close-button"]:after': {
            pointerEvents: 'auto',
          },
          '[class*="balloon__close-button"]::before, [class*="balloon__close-button"]::after': {
            content: '""',
            position: 'absolute',
            left: '6px',
            top: '15px',
            width: '22px',
            height: '2px',
            borderRadius: '2px',
            background: darkMode ? '#f8fafc !important' : '#0f172a !important',
            boxShadow: darkMode ? '0 0 3px rgba(15, 23, 42, 0.95)' : 'none',
          },
          '[class*="balloon__close-button"]::before': {
            transform: 'rotate(45deg)',
          },
          '[class*="balloon__close-button"]::after': {
            transform: 'rotate(-45deg)',
          },
        }}
      />

      <Container maxWidth="lg" sx={{ py: 2 }}>
      {/* Современный header с градиентом */}
      <Box sx={{
        textAlign: 'center',
        mb: 4,
        p: 4,
        background: darkMode
          ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 3,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="7" cy="7" r="2"/%3E%3Ccircle cx="27" cy="7" r="2"/%3E%3Ccircle cx="47" cy="7" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3,
        }
      }}>
        {/* Кнопка переключения темы */}
        <IconButton
          onClick={toggleDarkMode}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            color: 'white',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
            },
          }}
        >
          {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
        </IconButton>

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: { xs: '2rem', sm: '2.5rem' },
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              mb: 1,
              ...(darkMode && {
                '& .text-glow': {
                  background: 'linear-gradient(90deg, #ff8c00 0%, #ffa500 20%, #ffd700 40%, #ffffff 70%, #e3f2fd 100%) !important',
                  backgroundClip: 'text !important',
                  WebkitBackgroundClip: 'text !important',
                  WebkitTextFillColor: 'transparent !important'
                }
              }),
            }}
          >
            <Box sx={{
              position: 'relative',
              display: 'inline-block',
              fontSize: 'inherit',
              '&:hover .truck': {
                animation: 'bounce 0.6s ease-in-out infinite'
              },
              '&:hover .exhaust': {
                animation: 'smoke 1s ease-out infinite'
              },
              ...(darkMode && {
                '& .headlight': {
                  opacity: 0.8
                }
              }),
              '@keyframes bounce': {
                '0%, 100%': { transform: 'translateY(0) translateX(0)' },
                '25%': { transform: 'translateY(-1px) translateX(0.5px)' },
                '50%': { transform: 'translateY(0) translateX(1px)' },
                '75%': { transform: 'translateY(-0.5px) translateX(0.5px)' }
              },
              '@keyframes smoke': {
                '0%': { opacity: 0, transform: 'translateX(0) scale(0.5)' },
                '50%': { opacity: 0.7, transform: 'translateX(-8px) scale(1)' },
                '100%': { opacity: 0, transform: 'translateX(-16px) scale(1.5)' }
              },
              '@keyframes headlightFlash': {
                '0%, 30%': { opacity: 0 },
                '40%, 90%': { opacity: 0.8 },
                '100%': { opacity: 0 }
              },
              '@keyframes orangeGlow': {
                '0%, 30%': {
                  background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
                  textShadow: 'none'
                },
                '40%, 90%': {
                  background: 'linear-gradient(45deg, #ff8c00 30%, #ffd700 90%)',
                  textShadow: '0 0 10px rgba(255,140,0,0.5)'
                },
                '100%': {
                  background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
                  textShadow: 'none'
                }
              }
            }}>
              <LocalShippingIcon className="truck" sx={{
                fontSize: 'inherit',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                position: 'relative'
              }} />

              {/* Пучок света от фар */}
              <Box className="headlight" sx={{
                position: 'absolute',
                top: '20%',
                left: '100%',
                width: '80px',
                height: '40px',
                background: 'linear-gradient(90deg, rgba(255,165,0,0.6) 0%, rgba(255,215,0,0.4) 50%, transparent 100%)',
                clipPath: 'polygon(0 50%, 100% 0%, 100% 100%)',
                opacity: 0,
                filter: 'blur(1px)',
                pointerEvents: 'none'
              }} />

              {/* Выхлоп */}
              <Box className="exhaust" sx={{
                position: 'absolute',
                top: '40%',
                right: '85%',
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.6)',
                opacity: 0
              }} />
              <Box className="exhaust" sx={{
                position: 'absolute',
                top: '35%',
                right: '88%',
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.4)',
                opacity: 0,
                animationDelay: '0.3s'
              }} />
              <Box className="exhaust" sx={{
                position: 'absolute',
                top: '45%',
                right: '90%',
                width: '3px',
                height: '3px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.3)',
                opacity: 0,
                animationDelay: '0.6s'
              }} />
            </Box>
            <Box component="span" className="text-glow" sx={{
              background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 800,
              letterSpacing: '1px',
              transition: 'all 0.3s ease'
            }}>FAIDA</Box>{' '}<Box component="span" sx={{
              background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 800,
              letterSpacing: '1px',
              transition: 'all 0.3s ease'
            }}>GROUP</Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.9,
              fontWeight: 400,
              fontSize: { xs: '1rem', sm: '1.2rem' },
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }}
          >
            Мониторинг доставок в реальном времени «{deploymentName()}»
          </Typography>
        </Box>
      </Box>

      {/* Выбор даты */}
      <Box sx={{
        mb: 3,
        p: { xs: 2, sm: 3 },
        backgroundColor: 'background.paper',
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{
            fontWeight: 600,
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <CalendarTodayIcon sx={{ fontSize: 'inherit' }} />
            Выбор даты маршрутов
          </Typography>
          <Typography
            variant="body2"
            color={status === 'success' ? 'success.main' : 'error.main'}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <FiberManualRecordIcon sx={{ fontSize: 12 }} />
            {status === 'success' ? 'Онлайн' : 'Офлайн'}
          </Typography>
        </Box>

        <Box sx={{
          display: 'flex',
          gap: { xs: 1, sm: 2 },
          alignItems: { xs: 'stretch', sm: 'center' },
          flexWrap: 'wrap',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'flex-start', sm: 'space-between' }
        }}>
          <Box sx={{
            display: 'flex',
            gap: { xs: 1, sm: 2 },
            alignItems: { xs: 'stretch', sm: 'center' },
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
          <TextField
            placeholder="Дата маршрутов"
            value=""
            onClick={(e) => {
              setCalendarAnchor(e.currentTarget);
              setCalendarOpen(true);
            }}
            InputProps={{
              readOnly: true,
              inputComponent: React.forwardRef((props, ref) => (
                <Box
                  ref={ref}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    width: '100%',
                    cursor: 'pointer'
                  }}
                >
                  <EditCalendarIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {format(new Date(selectedDate), 'dd.MM.yyyy')}
                  </Typography>
                </Box>
              )),
              sx: {
                cursor: 'pointer'
              }
            }}
            size="small"
            sx={{
              minWidth: { xs: '100%', sm: 180 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                background: darkMode
                  ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.5) 0%, rgba(51, 65, 85, 0.5) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(248, 250, 252, 0.8) 100%)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: darkMode
                    ? '0 4px 12px rgba(129, 140, 248, 0.2)'
                    : '0 4px 12px rgba(79, 70, 229, 0.15)',
                  transform: 'translateY(-1px)',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  }
                },
                '&.Mui-focused': {
                  boxShadow: darkMode
                    ? '0 6px 20px rgba(129, 140, 248, 0.3)'
                    : '0 6px 20px rgba(79, 70, 229, 0.25)',
                  background: darkMode
                    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(51, 65, 85, 0.8) 100%)'
                    : 'linear-gradient(135deg, rgba(79, 70, 229, 0.02) 0%, rgba(6, 182, 212, 0.02) 100%)',
                  transform: 'translateY(-2px)',
                }
              },
              '& .MuiInputLabel-root': {
                color: 'text.secondary',
                fontWeight: 500,
                '&.Mui-focused': {
                  color: 'primary.main',
                  fontWeight: 600,
                }
              },
              '& input': {
                cursor: 'pointer',
                fontWeight: 500,
                color: darkMode ? '#e2e8f0' : '#334155',
              }
            }}
          />

          {/* Кастомный календарь в Popover */}
          <Popover
            open={calendarOpen}
            anchorEl={calendarAnchor}
            onClose={() => setCalendarOpen(false)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
                background: darkMode
                  ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
                  : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: `1px solid ${darkMode ? 'rgba(129, 140, 248, 0.2)' : 'rgba(79, 70, 229, 0.1)'}`,
                overflow: 'hidden',
              }
            }}
          >
            {/* Заголовок календаря */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px 16px',
              background: darkMode
                ? 'linear-gradient(135deg, rgba(129, 140, 248, 0.15) 0%, rgba(6, 182, 212, 0.15) 100%)'
                : 'linear-gradient(135deg, rgba(79, 70, 229, 0.08) 0%, rgba(6, 182, 212, 0.08) 100%)',
              borderBottom: `1px solid ${darkMode ? 'rgba(129, 140, 248, 0.1)' : 'rgba(79, 70, 229, 0.08)'}`,
            }}>
              <Typography variant="body2" sx={{
                color: darkMode ? '#e2e8f0' : '#334155',
                fontWeight: 600,
                fontSize: '14px',
              }}>
                Выберите дату
              </Typography>
            </Box>
            <DayPicker
              mode="single"
              selected={new Date(selectedDate)}
              onSelect={(date) => {
                if (date) {
                  setSelectedDate(format(date, 'yyyy-MM-dd'));
                  setCalendarOpen(false);
                }
              }}
              locale={ru}
              modifiersStyles={{
                selected: {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #06b6d4 100%)',
                  color: 'white',
                  fontWeight: 600,
                },
                today: {
                  background: darkMode
                    ? 'rgba(129, 140, 248, 0.3)'
                    : 'rgba(79, 70, 229, 0.15)',
                  color: darkMode ? '#ffffff' : '#4f46e5',
                  fontWeight: 600,
                }
              }}
              styles={{
                root: {
                  '--rdp-cell-size': '40px',
                  '--rdp-accent-color': darkMode ? '#818cf8' : '#4f46e5',
                  '--rdp-background-color': darkMode ? '#1e293b' : '#ffffff',
                  color: darkMode ? '#e2e8f0' : '#334155',
                  fontSize: '14px',
                } as React.CSSProperties,
                month: {
                  background: darkMode
                    ? 'linear-gradient(135deg, rgba(129, 140, 248, 0.1) 0%, rgba(6, 182, 212, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(79, 70, 229, 0.05) 0%, rgba(6, 182, 212, 0.05) 100%)',
                  borderRadius: '12px 12px 0 0',
                  padding: '16px',
                },
                day: {
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                },
                day_button: {
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                }
              }}
            />
          </Popover>
          <Button
            variant="contained"
            onClick={() => loadRoutesForDate(selectedDate)}
            disabled={loading}
            size="small"
            sx={{
              width: { xs: '100%', sm: '150px' },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            {loading ? (
              <>
                <CircularProgress
                  size={16}
                  sx={{
                    color: 'white',
                    mr: 1,
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      '0%': {
                        transform: 'rotate(0deg)',
                      },
                      '100%': {
                        transform: 'rotate(360deg)',
                      },
                    },
                  }}
                />
                Обновление...
              </>
            ) : 'Обновить'}
          </Button>
          </Box>

          <Box sx={{
            display: 'flex',
            gap: { xs: 1, sm: 1.5 },
            alignItems: { xs: 'stretch', sm: 'center' },
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', sm: 'row' },
            width: { xs: '100%', sm: 'auto' }
          }}>
          {availableDates.slice(0, 3).map((date) => (
            <Button
              key={date}
              variant="outlined"
              onClick={() => setSelectedDate(date)}
              size="small"
              color={selectedDate === date ? 'primary' : 'inherit'}
              sx={{
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              {new Date(date + 'T00:00:00').toLocaleDateString('ru-RU', {
                day: 'numeric',
                month: 'long'
              })}
            </Button>
          ))}
          </Box>
        </Box>

        {lastUpdate && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Последнее обновление: {lastUpdate}
          </Typography>
        )}
      </Box>

      {/* Современная статистика маршрутов */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(auto-fit, minmax(220px, 1fr))' },
        gap: { xs: 2, sm: 3 },
        mb: 4
      }}>
        <Box sx={{
          p: { xs: 2, sm: 3 },
          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
          color: 'white',
          borderRadius: 3,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(79, 70, 229, 0.3)',
          transition: 'transform 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -50,
            right: -50,
            width: 100,
            height: 100,
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
          }
        }}>
          <LocalShippingIcon sx={{ fontSize: { xs: 32, sm: 40 }, mb: 1, opacity: 0.9 }} />
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '1.5rem', sm: '3rem' } }}>{routes.length}</Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>Всего маршрутов</Typography>
        </Box>

        <Box sx={{
          p: { xs: 2, sm: 3 },
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          borderRadius: 3,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
          transition: 'transform 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -50,
            right: -50,
            width: 100,
            height: 100,
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
          }
        }}>
          <CheckCircleIcon sx={{ fontSize: { xs: 32, sm: 40 }, mb: 1, opacity: 0.9 }} />
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '1.5rem', sm: '3rem' } }}>{routes.filter(r => mapRouteStatus(r.routeStatus) === 'Завершено').length}</Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>Завершено</Typography>
        </Box>

        <Box sx={{
          p: { xs: 2, sm: 3 },
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: 'white',
          borderRadius: 3,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(245, 158, 11, 0.3)',
          transition: 'transform 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -50,
            right: -50,
            width: 100,
            height: 100,
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
          }
        }}>
          <DirectionsCarIcon sx={{ fontSize: { xs: 32, sm: 40 }, mb: 1, opacity: 0.9 }} />
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '1.5rem', sm: '3rem' } }}>
            {(() => {
              const today = format(new Date(), 'yyyy-MM-dd');
              const inProgressRoutes = routes.filter(r => mapRouteStatus(r.routeStatus) === 'В пути');
              const actual = inProgressRoutes.filter(r => r.routeDate >= today).length;
              const overdue = inProgressRoutes.filter(r => r.routeDate < today).length;
              return `${actual} / ${overdue}`;
            })()}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>В пути / Просрочено</Typography>
        </Box>

        <Box sx={{
          p: { xs: 2, sm: 3 },
          background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
          color: 'white',
          borderRadius: 3,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(6, 182, 212, 0.3)',
          transition: 'transform 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -50,
            right: -50,
            width: 100,
            height: 100,
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
          }
        }}>
          <HourglassEmptyIcon sx={{ fontSize: { xs: 32, sm: 40 }, mb: 1, opacity: 0.9 }} />
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '1.5rem', sm: '3rem' } }}>{routes.filter(r => mapRouteStatus(r.routeStatus) === 'Ожидание').length}</Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>Ожидание</Typography>
        </Box>
      </Box>

      {/* Статистика доставок */}
      {routes.length > 0 && (() => {
        const allDeliveries = routes.flatMap(route => route.deliveries || []);
        const totalDeliveries = allDeliveries.length;

        // Подсчет по новой логике статусов на основе отфильтрованных маршрутов
        const today = format(new Date(), 'yyyy-MM-dd');
        const deliveriesWithRouteDate = routes.flatMap(route =>
          (route.deliveries || []).map(d => ({ ...d, routeDate: route.routeDate }))
        );
        const notAttemptedDeliveries = deliveriesWithRouteDate.filter(d => !d.deliveryReport?.trim());
        const notAttemptedActual = notAttemptedDeliveries.filter(d => d.routeDate >= today).length;
        const notAttemptedOverdue = notAttemptedDeliveries.filter(d => d.routeDate < today).length;

        const delivered = allDeliveries.filter(d => d.deliveryReport?.includes('Доставлено')).length;
        const rejected = allDeliveries.filter(d => d.deliveryReport?.includes('Попытка доставки')).length;
        const totalWeight = allDeliveries.reduce((sum, d) => sum + (d.weight || 0), 0);
        const totalPackages = allDeliveries.reduce((sum, d) => {
          if (d.packages) {
            // Извлекаем все числа из строки packages (например "15к2п" -> [15, 2])
            const numbers = d.packages.match(/\d+/g) || [];
            // Суммируем числа внутри одной строки
            const packagesSum = numbers.reduce((s, n) => s + parseInt(n, 10), 0);
            return sum + packagesSum;
          }
          return sum;
        }, 0);

        return (
          <Box sx={{
            mb: 3,
            p: { xs: 2, sm: 3 },
            backgroundColor: 'background.paper',
            borderRadius: 3,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
          }}>
            <Typography variant="h6" sx={{
              mb: 2,
              color: 'primary.main',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <InventoryIcon sx={{ fontSize: 'inherit' }} />
              Статистика доставок
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(auto-fit, minmax(150px, 1fr))' },
              gap: { xs: 1.5, sm: 2 }
            }}>
              <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'white', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="h5">{totalDeliveries}</Typography>
                <Typography variant="body2">Всего точек</Typography>
              </Box>
              <Box sx={{ p: 2, backgroundColor: 'success.main', color: 'white', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="h5">{delivered}</Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                  <CheckCircleIcon sx={{ fontSize: 16 }} />
                  Доставлено
                </Typography>
              </Box>
              <Box sx={{ p: 2, backgroundColor: 'warning.main', color: 'white', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="h5">{notAttemptedActual} / {notAttemptedOverdue}</Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                  <HourglassEmptyIcon sx={{ fontSize: 16 }} />
                  Доставляется / Просрочено
                </Typography>
              </Box>
              <Box sx={{ p: 2, backgroundColor: 'error.main', color: 'white', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="h5">{rejected}</Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                  <CancelIcon sx={{ fontSize: 16 }} />
                  Не приняли
                </Typography>
              </Box>
              <Box sx={{ p: 2, backgroundColor: 'info.main', color: 'white', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="h5">{totalWeight.toFixed(1)} / {totalPackages}</Typography>
                <Typography variant="body2">Общий вес (кг) / мест (шт)</Typography>
              </Box>
            </Box>
          </Box>
        );
      })()}

      {/* Навигация по страницам */}
      <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{ textAlign: 'center', mb: 1 }}>
              {isToday(selectedDate) && <Typography role="status" color={vehiclePositionsMeta?.staleVehicles || vehiclePositionsMeta?.missingVehicles || vehiclePositionsMeta?.errors?.length || vehiclePositionsMeta?.error ? 'warning.main' : 'text.secondary'} variant="body2">{gpsStatusText(vehiclePositionsMeta, vehiclePositions.length)}</Typography>}
              {selectedDate > moscowDate() && <Typography variant="body2" color="text.secondary">План на будущую дату. Текущие GPS-позиции здесь не показываются.</Typography>}
            </Box>
            <Tabs
          value={currentPage}
          onChange={(_, newValue) => setCurrentPage(newValue)}
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            '& .MuiTab-root': {
              minWidth: { xs: 80, sm: 120 },
              fontWeight: 500
            }
          }}
        >
          <Tab
            icon={<ListIcon />}
            label="Список"
            iconPosition="start"
          />
          <Tab
            icon={<MapIcon />}
            label="Карта"
            iconPosition="start"
          />
          <Tab
            icon={<ViewCompactIcon />}
            label="Гибрид"
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Контент страниц */}
      {currentPage === 0 && (
        // Страница "Список"
        <>
          {/* Панель управления и фильтр */}
          {routes.length > 0 && (
              <Box sx={{
                mb: 2,
                p: 2,
                backgroundColor: 'background.paper',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap'
              }}>
                {listFilteredRoutes.length > 0 && (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} />}
                      onClick={expandAllRoutes}
                      sx={{ fontSize: '0.75rem', borderRadius: 2 }}
                    >
                      Развернуть все
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ExpandMoreIcon />}
                      onClick={collapseAllRoutes}
                      sx={{ fontSize: '0.75rem', borderRadius: 2 }}
                    >
                      Свернуть все
                    </Button>
                  </>
                )}

                <TextField
                  placeholder="Поиск по клиенту..."
                  value={listClientFilter}
                  onChange={(e) => setListClientFilter(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                    sx: { borderRadius: 2 }
                  }}
                  sx={{ minWidth: { xs: '100%', sm: 250 } }}
                />
                {listClientFilter && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setListClientFilter('')}
                    startIcon={<ClearIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    Очистить
                  </Button>
                )}

                <Typography variant="body2" color="text.secondary">
                  {listClientFilter ?
                    `Найдено: ${listFilteredRoutes.length} из ${routes.length} маршрут(ов)` :
                    `Найдено: ${routes.length} маршрут(ов)`
                  }
                </Typography>
              </Box>
            )}

          {loading && (
            <Box sx={{ mx: 2 }}>
              {/* Современные скелетоны карточек маршрутов */}
              {[1, 2, 3].map((index) => (
                <Paper
                  key={index}
                  sx={{
                    mb: 3,
                    borderRadius: 4,
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    overflow: 'hidden',
                    position: 'relative',
                    background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.02) 0%, rgba(6, 182, 212, 0.02) 100%)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: `linear-gradient(135deg, rgba(79, 70, 229, 0.3) 0%, rgba(6, 182, 212, 0.3) 100%)`,
                      animation: 'shimmer 1.5s ease-in-out infinite',
                      '@keyframes shimmer': {
                        '0%': { opacity: 0.3 },
                        '50%': { opacity: 0.7 },
                        '100%': { opacity: 0.3 },
                      },
                    },
                  }}
                >
                  <Box sx={{ p: 3 }}>
                    {/* Заголовок карточки */}
                    <Box sx={{
                      background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.08) 0%, rgba(6, 182, 212, 0.08) 100%)',
                      borderRadius: 3,
                      p: 2,
                      mb: 2,
                      position: 'relative',
                      overflow: 'hidden',
                    }}>
                      <Skeleton
                        variant="text"
                        width="40%"
                        height={28}
                        sx={{
                          mb: 1,
                          animation: 'wave 1.5s ease-in-out 0.5s infinite',
                        }}
                      />
                      <Skeleton
                        variant="text"
                        width="60%"
                        height={20}
                        sx={{
                          mb: 0.5,
                          animation: 'wave 1.5s ease-in-out 0.7s infinite',
                        }}
                      />
                      <Skeleton
                        variant="text"
                        width="30%"
                        height={20}
                        sx={{
                          animation: 'wave 1.5s ease-in-out 0.9s infinite',
                        }}
                      />
                    </Box>

                    {/* Информационные мини-карточки */}
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' },
                      gap: 2,
                    }}>
                      {[1, 2, 3, 4].map((cardIndex) => (
                        <Box
                          key={cardIndex}
                          sx={{
                            background: 'rgba(255, 255, 255, 0.7)',
                            borderRadius: 2,
                            p: 1.5,
                            border: '1px solid rgba(226, 232, 240, 0.5)',
                          }}
                        >
                          <Skeleton
                            variant="circular"
                            width={24}
                            height={24}
                            sx={{
                              mb: 1,
                              animation: `wave 1.5s ease-in-out ${cardIndex * 0.2}s infinite`,
                            }}
                          />
                          <Skeleton
                            variant="text"
                            width="80%"
                            height={16}
                            sx={{
                              animation: `wave 1.5s ease-in-out ${cardIndex * 0.2 + 0.1}s infinite`,
                            }}
                          />
                          <Skeleton
                            variant="text"
                            width="60%"
                            height={14}
                            sx={{
                              animation: `wave 1.5s ease-in-out ${cardIndex * 0.2 + 0.2}s infinite`,
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Paper>
              ))}

              {/* Центральный индикатор загрузки */}
              <Box sx={{
                textAlign: 'center',
                py: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Box sx={{ position: 'relative', mb: 2 }}>
                  <CircularProgress
                    size={32}
                    thickness={3}
                    sx={{
                      color: 'primary.main',
                      animationDuration: '1.4s',
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{
                  color: 'text.secondary',
                  textAlign: 'center',
                  animation: 'fadeInOut 2s ease-in-out infinite',
                  '@keyframes fadeInOut': {
                    '0%, 100%': { opacity: 0.7 },
                    '50%': { opacity: 1 },
                  },
                }}>
                  Загрузка маршрутов за {selectedDate}...
                </Typography>
              </Box>
            </Box>
          )}

          {routes.length === 0 && !loading && status !== 'loading' && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                Нет маршрутов за {selectedDate}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Попробуйте выбрать другую дату
              </Typography>
            </Box>
          )}

          {routes.length > 0 && listFilteredRoutes.length === 0 && listClientFilter && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                Клиент "{listClientFilter}" не найден
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Попробуйте изменить запрос или очистить фильтр
              </Typography>
            </Box>
          )}

          {listFilteredRoutes.map((route, index) => (
            <RouteCard
              key={index}
              route={route}
              clientFilter={listClientFilter}
              darkMode={darkMode}
              isExpanded={expandedRoutes[route.routeNumber] === true}
              onToggleExpanded={() => {
                setExpandedRoutes(prev => ({
                  ...prev,
                  [route.routeNumber]: !(prev[route.routeNumber] === true)
                }));
              }}
            />
          ))}
        </>
      )}

      {currentPage === 1 && (
        // Страница "Карта"
        <>
          <Box sx={{
            mb: 2,
            p: 2,
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap'
          }}>
            <TextField
              placeholder="Поиск по клиенту на карте..."
              value={mapClientFilter}
              onChange={(e) => setMapClientFilter(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                sx: { borderRadius: 2 }
              }}
              sx={{ minWidth: { xs: '100%', sm: 250 } }}
            />
            {mapClientFilter && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setMapClientFilter('')}
                startIcon={<ClearIcon />}
                sx={{ borderRadius: 2 }}
              >
                Очистить
              </Button>
            )}
            <Typography variant="body2" color="text.secondary">
              {mapClientFilter ?
                `Показано: ${mapFilteredRoutes.flatMap(r => r.deliveries || []).filter(d => d.location?.latitude && d.location?.longitude && d.client?.toLowerCase().includes(mapClientFilter.toLowerCase())).length} точек` :
                `Показано: ${mapFilteredRoutes.flatMap(r => r.deliveries || []).filter(d => d.location?.latitude && d.location?.longitude).length} точек`
              }
            </Typography>
          </Box>

          <Box sx={{ height: '65vh', borderRadius: 2, overflow: 'hidden' }}>
            <DeliveryMap routes={mapFilteredRoutes} clientFilter={mapClientFilter} mapLoading={mapLoading} setMapLoading={setMapLoading} darkMode={darkMode} vehiclePositions={vehiclePositions} selectedTrack={selectedTrack} onShowRouteTrack={loadRouteTrack} />
          </Box>
        </>
      )}

      {currentPage === 2 && (
        // Страница "Гибрид"
        <>
          <Box sx={{
            mb: 2,
            p: 2,
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap'
          }}>
            {/* Кнопки управления чекбоксами */}
            <Button
              variant="outlined"
              size="small"
              onClick={checkAllHybridRoutes}
              startIcon={<CheckBoxIcon />}
              sx={{ borderRadius: 2, fontSize: '0.75rem' }}
            >
              Выделить все
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={uncheckAllHybridRoutes}
              startIcon={<CheckBoxOutlineBlankIcon />}
              sx={{ borderRadius: 2, fontSize: '0.75rem' }}
            >
              Снять все
            </Button>

            <TextField
              placeholder="Поиск по клиенту в гибридном режиме..."
              value={hybridClientFilter}
              onChange={(e) => setHybridClientFilter(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                sx: { borderRadius: 2 }
              }}
              sx={{ minWidth: { xs: '100%', sm: 250 } }}
            />
            {hybridClientFilter && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setHybridClientFilter('')}
                startIcon={<ClearIcon />}
                sx={{ borderRadius: 2 }}
              >
                Очистить
              </Button>
            )}

            <Typography variant="body2" color="text.secondary">
              {hybridClientFilter ?
                `Найдено: ${hybridFilteredRoutes.length} маршрут(ов)` :
                `Показано: ${hybridFilteredRoutes.length} маршрут(ов)`
              }
            </Typography>
          </Box>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '3fr 7fr' },
            gridTemplateRows: { xs: 'auto 60vh', md: '1fr' },
            gap: 2,
            height: { xs: 'auto', md: '65vh' }
          }}>
            {/* Список (30%) */}
            <Box sx={{
              backgroundColor: 'background.paper',
              borderRadius: 2,
              p: 2,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              overflow: 'auto'
            }}>
              <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                Маршруты ({hybridFilteredRoutes.length})
              </Typography>
              {hybridFilteredRoutes.map((route, index) => {
                const isExpanded = expandedHybridRoutes[route.routeNumber] || false;
                const isChecked = checkedHybridRoutes[route.routeNumber] || false;
                // Находим индекс в полном отсортированном списке для стабильного цвета
                const routeIndex = routes.findIndex(r => r.routeNumber === route.routeNumber);
                const routeColor = getRouteColorCSS(routeIndex);
                const vehiclePosition = vehiclePositions.find(vehicle =>
                  String(vehicle.routeNumber) === String(route.routeNumber)
                );
                const driverInsertIndex = getDriverInsertIndex(route, vehiclePosition);
                const vehicleSpeed = Number.isFinite(Number(vehiclePosition?.speed))
                  ? Math.round(Number(vehiclePosition.speed))
                  : null;
                const trackKey = `${route.routeNumber}-${selectedDate}`;
                const isTrackLoading = trackLoadingKey === trackKey;
                const isTrackActive = String(selectedTrack?.routeNumber) === String(route.routeNumber);
                const driverPositionRow = vehiclePosition ? (
                  <Box
                    key={`driver-position-${route.routeNumber}`}
                    sx={{
                      p: 1,
                      borderBottom: 1,
                      borderBottomColor: 'grey.100',
                      cursor: 'pointer',
                      backgroundColor: darkMode ? 'rgba(79, 70, 229, 0.16)' : 'rgba(79, 70, 229, 0.08)',
                      borderLeft: 3,
                      borderLeftColor: routeColor,
                      '&:hover': {
                        backgroundColor: darkMode ? 'rgba(79, 70, 229, 0.24)' : 'rgba(79, 70, 229, 0.14)'
                      },
                      transition: 'background-color 0.2s ease'
                    }}
                    onClick={() => {
                      setCheckedHybridRoutes(prev => ({
                        ...prev,
                        [route.routeNumber]: true
                      }));
                      setActiveDeliveryPoint({
                        routeNumber: route.routeNumber,
                        invoiceNumber: `vehicle-${vehiclePosition.unitId || route.routeNumber}`,
                        deliveryId: `vehicle-${vehiclePosition.unitId || route.routeNumber}`,
                        coordinates: [Number(vehiclePosition.lat), Number(vehiclePosition.lon)]
                      });
                    }}
                  >
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '0.75rem'
                    }}>
                      <Box sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: routeColor,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <LocalShippingIcon sx={{ fontSize: 14 }} />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: 'text.primary',
                          fontSize: '0.72rem',
                          flexShrink: 0
                        }}
                      >
                        {vehiclePosition.driver || route.driver || 'Водитель'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                          lineHeight: 1.2,
                          wordBreak: 'break-word'
                        }}
                      >
                        {vehicleSpeed !== null ? `${vehicleSpeed} км/ч` : 'скорость неизвестна'}
                      </Typography>
                      <IconButton
                        size="small"
                        title="Показать трек"
                        onClick={(e) => {
                          e.stopPropagation();
                          loadRouteTrack(route, routeColor);
                        }}
                        disabled={isTrackLoading || !route.transport}
                        sx={{
                          ml: 'auto',
                          width: 28,
                          height: 28,
                          color: isTrackActive ? routeColor : 'text.secondary',
                          backgroundColor: isTrackActive ? `${routeColor}22` : 'transparent',
                        }}
                      >
                        {isTrackLoading ? <CircularProgress size={16} /> : <DirectionsIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </Box>
                  </Box>
                ) : null;

                return (
                  <Box key={index} sx={{
                    mb: 1,
                    backgroundColor: 'grey.50',
                    borderRadius: 1,
                    borderLeft: 4,
                    borderLeftColor: routeColor,
                    overflow: 'hidden'
                  }}>
                    {/* Заголовок маршрута */}
                    <Box sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 28,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flexShrink: 0,
                            gap: 0.25,
                            mt: -0.5,
                          }}
                        >
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) => {
                              setCheckedHybridRoutes(prev => ({
                                ...prev,
                                [route.routeNumber]: e.target.checked
                              }));
                            }}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              p: 0.5,
                              '& .MuiSvgIcon-root': { fontSize: 18 }
                            }}
                          />
                          <IconButton
                            size="small"
                            title="Показать трек"
                            onClick={(e) => {
                              e.stopPropagation();
                              loadRouteTrack(route, routeColor);
                            }}
                            disabled={isTrackLoading || !route.transport}
                            sx={{
                              p: 0.5,
                              width: 28,
                              height: 28,
                              color: isTrackActive ? routeColor : 'text.secondary',
                              backgroundColor: isTrackActive ? `${routeColor}22` : 'transparent',
                              '&:hover': {
                                backgroundColor: isTrackActive ? `${routeColor}33` : 'action.hover',
                              },
                            }}
                          >
                            {isTrackLoading ? <CircularProgress size={16} /> : <DirectionsIcon sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </Box>
                        <Box
                          sx={{
                            cursor: 'pointer',
                            flex: 1,
                            minWidth: 0,
                            '&:hover': {
                              '& .route-text': {
                                color: 'primary.main'
                              }
                            }
                          }}
                          onClick={() => {
                            setExpandedHybridRoutes(prev => ({
                              ...prev,
                              [route.routeNumber]: !prev[route.routeNumber]
                            }));
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              className="route-text"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                transition: 'color 0.2s',
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Маршрут №{route.routeNumber}
                            </Typography>
                            {route.hasOutOfRouteDeliveries && (
                              <Chip
                                label="Вне маршрута"
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ height: 18, fontSize: '0.6rem', flexShrink: 0 }}
                              />
                            )}
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: '0.72rem', flexShrink: 0 }}
                            >
                              {route.deliveries?.length || 0} точек
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.25 }}>
                            {route.driver}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          cursor: 'pointer',
                          p: 0.5,
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                        onClick={() => {
                          setExpandedHybridRoutes(prev => ({
                            ...prev,
                            [route.routeNumber]: !prev[route.routeNumber]
                          }));
                        }}
                      >
                        <ExpandMoreIcon sx={{
                          fontSize: 18,
                          color: 'text.secondary',
                          transition: 'transform 0.2s ease-in-out',
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                        }} />
                      </Box>
                    </Box>

                    {/* Список точек доставки */}
                    {isExpanded && route.deliveries && route.deliveries.length > 0 && (
                      <Box sx={{
                        backgroundColor: 'background.paper',
                        borderTop: 1,
                        borderTopColor: 'divider'
                      }}>
                        {route.deliveries.map((delivery: any, deliveryIndex: number) => {
                          // Проверяем, является ли эта строка активной
                          const isActiveRow = activeDeliveryPoint &&
                            activeDeliveryPoint.routeNumber === route.routeNumber &&
                            activeDeliveryPoint.invoiceNumber === delivery.invoiceNumber &&
                            activeDeliveryPoint.deliveryId === delivery.id;

                          return (
                            <React.Fragment key={`${route.routeNumber}-${delivery.invoiceNumber}-${delivery.id || deliveryIndex}`}>
                              {driverInsertIndex === deliveryIndex && driverPositionRow}
                              <Box
                                sx={{
                                  p: 1,
                                  borderBottom: deliveryIndex < route.deliveries.length - 1 || driverInsertIndex === route.deliveries.length ? 1 : 0,
                                  borderBottomColor: 'grey.100',
                                  cursor: 'pointer',
                                  backgroundColor: isActiveRow ? 'primary.light' : 'transparent',
                                  '&:hover': {
                                    backgroundColor: isActiveRow ? 'primary.light' : 'grey.50'
                                  },
                                  transition: 'background-color 0.2s ease'
                                }}
                                onClick={() => {
                                  // Получаем координаты точки доставки
                                  const coordinates = delivery.location?.latitude && delivery.location?.longitude
                                    ? [parseFloat(delivery.location.latitude), parseFloat(delivery.location.longitude)] as [number, number]
                                    : undefined;

                                  setCheckedHybridRoutes(prev => ({
                                    ...prev,
                                    [route.routeNumber]: true
                                  }));

                                  // Устанавливаем активную точку
                                  setActiveDeliveryPoint({
                                    routeNumber: route.routeNumber,
                                    invoiceNumber: delivery.invoiceNumber,
                                    deliveryId: delivery.id,
                                    coordinates
                                  });
                                }}
                              >
                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  fontSize: '0.75rem'
                                }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      backgroundColor: routeColor,
                                      color: 'white',
                                      borderRadius: '50%',
                                      width: 18,
                                      height: 18,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.6rem',
                                      fontWeight: 600,
                                      flexShrink: 0
                                    }}
                                  >
                                    {delivery.id}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.secondary',
                                      fontSize: '0.7rem',
                                      fontFamily: 'monospace',
                                      flexShrink: 0
                                    }}
                                  >
                                    {delivery.invoiceNumber}
                                  </Typography>
                                  {delivery.outOfRoute && (
                                    <Chip
                                      label="Вне маршрута"
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      sx={{ height: 18, fontSize: '0.6rem', flexShrink: 0 }}
                                    />
                                  )}
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '0.75rem',
                                      lineHeight: 1.2,
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {delivery.client}
                                  </Typography>
                                </Box>
                              </Box>
                            </React.Fragment>
                          );
                        })}
                        {driverInsertIndex === route.deliveries.length && driverPositionRow}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Карта (70%) */}
            <Box sx={{
              borderRadius: 2,
              overflow: 'hidden'
            }}>
              <DeliveryMap routes={hybridMapRoutes} allRoutes={routes} clientFilter={hybridClientFilter} activeDeliveryPoint={activeDeliveryPoint} mapLoading={mapLoading} setMapLoading={setMapLoading} darkMode={darkMode} vehiclePositions={vehiclePositions} selectedTrack={selectedTrack} onShowRouteTrack={loadRouteTrack} />
            </Box>
          </Box>
        </>
      )}

      </Container>

      {/* Футер с информацией о разработчиках */}
      <Box
        component="footer"
        sx={{
          mt: 'auto',
          py: { xs: 2, sm: 3 },
          background: darkMode
            ? 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)'
            : 'linear-gradient(135deg, #4f46e5 0%, #06b6d4 50%, #0891b2 100%)',
          borderTop: `1px solid ${darkMode ? 'rgba(129, 140, 248, 0.2)' : 'rgba(255, 255, 255, 0.2)'}`,
          textAlign: 'center',
          boxShadow: darkMode
            ? '0 -4px 20px rgba(0, 0, 0, 0.3)'
            : '0 -4px 20px rgba(79, 70, 229, 0.15)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 1, sm: 2 },
          }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
            <CodeIcon sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: { xs: 18, sm: 20 },
            }} />
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                fontWeight: 500,
              }}
            >
              © 2026 Faida IT Solutions
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: { xs: '0.7rem', sm: '0.8rem' },
              fontStyle: 'italic',
            }}
          >
            Разработано подразделением IT компании Faida Group
          </Typography>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          <Route path="/" element={<HomePage />} />
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
